import React, { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence, PanInfo } from "framer-motion";
import { useSocket } from "../hooks/useSocket";
import { sessionService } from "../services/session.service";
import { GameState, Card, PlayCardRequest, CardColor, CardValue, GameStatus } from "../types/game";
import { CardSVG } from "../cards";
import { ColorPicker } from "../components/ColorPicker";
import { HelpModal } from "../components/HelpModal";
import { LogModal } from "../components/LogModal";
import { FlyingCard, FlyingCardData } from "../components/FlyingCard";
import { DiscardPile } from "../components/DiscardPile";
import { DrawDeck } from "../components/DrawDeck";
import { CircularOpponentsLayout } from "../components/CircularOpponentsLayout";
import { TableArea } from "../components/TableArea";
import { triggerHaptic } from "../utils/haptics";
import { UnoCallAnimation } from "../components/UnoCallAnimation";
import { PenaltyAnimation } from "../components/PenaltyAnimation";
import { EndGameScreen } from "../components/EndGameScreen";

// Draw stack UI state (derived from server state only)
type DrawStackUI = {
  count: number;        // total cards to draw (2, 4, 6, ...)
  type: "+2" | "+4";
  active: boolean;
};

// UNO UX state (derived from server state only)
type UnoUXState = {
  needsReminder: boolean;  // Player has 2 cards, it's their turn, hasn't called UNO
  urgent: boolean;         // Player has 1 card, hasn't called UNO
  penaltyActive: boolean;  // UNO penalty was just applied
  successActive: boolean;  // UNO was successfully called
};

// Penalty animation state
type PenaltyState = {
  active: boolean;
  type: "draw2" | "draw4" | "missedUno" | null;
  cardsCount: number;
};

/**
 * ANIMATION SYSTEM: Penalty Card Play Sequence
 * 
 * When a +2 or +4 card is played, a 5-step animation sequence runs:
 * 
 * Step 1: Card Impact (~200ms)
 *   - Penalty card becomes sole focus with overshoot scale (1.0 → 1.1 → 1.0)
 *   - Color-based glow pulse (blue for +2, orange for +4)
 *   - All other UI elements dim to 40-50% opacity
 * 
 * Step 2: Target Indication (~200ms)
 *   - Affected avatar receives brief shake/pulse
 *   - Card count badge highlights with penalty color glow
 * 
 * Step 3: Draw Counter (~150ms)
 *   - Draw counter (+2/+4) appears NEAR the affected avatar
 *   - Pops in with scale animation (0.8 → 1.0)
 *   - Uses card color theme
 * 
 * Step 4: Card Draw (~300ms)
 *   - Cards animate from draw deck toward affected player
 *   - Travel along curved path (handled by FlyingCard component)
 *   - Draw animation batched visually (1-2 cards shown even if +4)
 * 
 * Step 5: Reset
 *   - Clear all penalty indicators
 *   - Restore normal table lighting
 *   - UI transitions to next state
 * 
 * Draw Chain Escalation:
 *   - When player responds with another penalty card, counter increments with bounce
 *   - Color glow intensifies slightly with each stack
 *   - Avoids repeating full "impact" animation - escalation feels faster
 * 
 * Accessibility:
 *   - Respects prefers-reduced-motion: replaces movement with opacity + scale only
 *   - Animations are interruptible if game state advances unexpectedly
 *   - No layout reflow during animations
 */
type PenaltyAnimationPhase = 
  | "idle"           // No animation
  | "cardImpact"     // Step 1: Card becomes focus, glow pulse
  | "targetIndication" // Step 2: Draw trail to affected player
  | "drawCounter"    // Step 3: Show counter near avatar
  | "cardDraw"       // Step 4: Cards moving to player
  | "reset";         // Step 5: Clear all indicators

type PenaltyAnimationState = {
  phase: PenaltyAnimationPhase;
  affectedPlayerId: string | null;
  penaltyType: "+2" | "+4" | null;
  count: number;
};

// Other player UNO call state
type OtherPlayerUnoCall = {
  playerId: string;
  playerName: string;
  active: boolean;
};

export function Game() {
  const { roomId } = useParams<{ roomId: string }>();
  const navigate = useNavigate();
  const socket = useSocket();
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [error, setError] = useState("");
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [pendingCardIndex, setPendingCardIndex] = useState<number | null>(null);
  const [canPlayDrawnCard, setCanPlayDrawnCard] = useState(false);
  const [isSpectator, setIsSpectator] = useState(false);
  const [hoveredCardIndex, setHoveredCardIndex] = useState<number | null>(null);
  const [draggedCardIndex, setDraggedCardIndex] = useState<number | null>(null);
  const [isOverDiscardPile, setIsOverDiscardPile] = useState(false);
  const [justDragged, setJustDragged] = useState(false);
  const [helpButtonHovered, setHelpButtonHovered] = useState(false);
  const [logButtonHovered, setLogButtonHovered] = useState(false);
  const [backButtonHovered, setBackButtonHovered] = useState(false);
  const [showLogModal, setShowLogModal] = useState(false);
  const [gameLogs, setGameLogs] = useState<Array<{ id: string; timestamp: Date; message: string }>>([]);
  const [shouldSnapBack, setShouldSnapBack] = useState<number | null>(null);
  const [flyingCard, setFlyingCard] = useState<FlyingCardData | null>(null);
  // Note: swipingCardIndex moved to swipe state section below
  const [drawStormCards, setDrawStormCards] = useState<FlyingCardData[]>([]);
  const [unoUXState, setUnoUXState] = useState<UnoUXState>({
    needsReminder: false,
    urgent: false,
    penaltyActive: false,
    successActive: false,
  });
  const [penaltyState, setPenaltyState] = useState<PenaltyState>({
    active: false,
    type: null,
    cardsCount: 0,
  });
  const [penaltyAnimationState, setPenaltyAnimationState] = useState<PenaltyAnimationState>({
    phase: "idle",
    affectedPlayerId: null,
    penaltyType: null,
    count: 0,
  });
  
  // Check for prefers-reduced-motion
  const prefersReducedMotion = useMemo(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }, []);
  const [otherPlayerUnoCall, setOtherPlayerUnoCall] = useState<OtherPlayerUnoCall | null>(null);
  const [sessionRole, setSessionRole] = useState<"active" | "passive">("active");
  const [handUIMode, setHandUIMode] = useState<"compact" | "expanded" | "focused">("compact");
  const [focusedCardIndex, setFocusedCardIndex] = useState<number | null>(null);
  const [gameOverState, setGameOverState] = useState<{ winnerId: string; winnerName?: string } | null>(null);
  const [gameStartTime] = useState<number>(Date.now());
  const previousGameStateRef = useRef<GameState | null>(null);
  const previousDirectionRef = useRef<string | null>(null);
  const [showDirectionIndicator, setShowDirectionIndicator] = useState(false);
  const previousHandSizeRef = useRef<number | null>(null);
  const discardPileRef = useRef<HTMLDivElement>(null);
  const draggedCardRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const cardWrapperRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const drawButtonRef = useRef<HTMLButtonElement>(null);
  const unoButtonRef = useRef<HTMLButtonElement>(null);
  const handContainerRef = useRef<HTMLDivElement>(null);
  const handSwipeStartY = useRef<number | null>(null);
  
  // Gesture intent detection for horizontal/vertical swipe
  type GestureIntent = "undecided" | "horizontal" | "vertical" | "tap";
  const gestureIntentRef = useRef<GestureIntent>("undecided");
  const gestureStartX = useRef<number | null>(null);
  const gestureStartY = useRef<number | null>(null);
  const horizontalOffsetRef = useRef<number>(0); // Horizontal scroll offset for compact mode
  
  // Horizontal offset state for card positioning (only update on gesture end)
  const [horizontalOffset, setHorizontalOffset] = useState<number>(0);
  
  // Card-level swipe state for smooth finger tracking
  const [swipingCardIndex, setSwipingCardIndex] = useState<number | null>(null);
  const [swipeOffset, setSwipeOffset] = useState<number>(0); // Current swipe offset during gesture
  const [currentGestureIntent, setCurrentGestureIntent] = useState<GestureIntent>("undecided"); // State for visual feedback
  const swipeStartTimeRef = useRef<number | null>(null);
  const swipeStartOffsetRef = useRef<number>(0);
  
  // Gesture thresholds (lowered for earlier detection)
  const HORIZONTAL_THRESHOLD = 8; // Reduced from 10 for earlier detection
  const VERTICAL_THRESHOLD = 12;
  const TAP_THRESHOLD = 5; // Maximum movement for tap (in pixels)
  const TAP_TIME_THRESHOLD = 200; // Maximum time for tap (in ms)
  
  // Force mobile layout for all devices (mobile-first design)
  const isMobile = useMemo(() => {
    // Always return true to use mobile layout on all devices
    return true;
  }, []);

  // Use userId from session instead of socketId (for reconnection support)
  const userId = sessionService.getUserId();

  // Calculate game timer - only show when countdown < threshold (e.g., < 30 seconds)
  const [gameTime, setGameTime] = useState(0);
  const TIMER_THRESHOLD = 30; // Only show timer when < 30 seconds
  useEffect(() => {
    if (!gameState || gameState.status !== GameStatus.PLAYING) return;
    
    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - gameStartTime) / 1000);
      setGameTime(elapsed);
    }, 1000);

    return () => clearInterval(interval);
  }, [gameState?.status, gameStartTime]);
  
  const showTimer = gameTime > 0 && gameTime < TIMER_THRESHOLD;

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  useEffect(() => {
    if (!roomId) {
      navigate("/");
      return;
    }

    // Kiểm tra localStorage và session để reconnect nếu page bị reload
    const savedRoomInfo = localStorage.getItem("uno_room");
    const session = sessionService.getSession();
    
    if (savedRoomInfo || session) {
      try {
        let roomInfo: any = null;
        if (savedRoomInfo) {
          roomInfo = JSON.parse(savedRoomInfo);
        }
        
        // Use session player name if available, otherwise use saved room info
        const playerName = session?.playerName || roomInfo?.playerName;
        
        if (roomId && playerName && (roomInfo?.roomId === roomId || session)) {
          // Page reload - tự động reconnect với session
          console.log("[Game] Page reload detected, reconnecting to room:", roomId);
          console.log("[Game] Using session:", session?.userId);
          
          // Đợi socket connect trước khi join room
          const attemptReconnect = () => {
            if (socket.isSocketConnected()) {
              socket.joinRoom(roomId, playerName);
            } else {
              // Đợi thêm một chút và thử lại
              setTimeout(attemptReconnect, 500);
            }
          };
          attemptReconnect();
        }
      } catch (e) {
        console.error("[Game] Error parsing saved room info:", e);
      }
    }

    const formatCard = (card: Card): string => {
      const colorNames: Record<CardColor, string> = {
        [CardColor.RED]: "Đỏ",
        [CardColor.BLUE]: "Xanh dương",
        [CardColor.GREEN]: "Xanh lá",
        [CardColor.YELLOW]: "Vàng",
        [CardColor.WILD]: "Đổi màu",
      };

      const valueNames: Record<string, string> = {
        [CardValue.SKIP]: "Bỏ lượt",
        [CardValue.REVERSE]: "Đảo chiều",
        [CardValue.DRAW2]: "Rút 2",
        [CardValue.WILD]: "",
        [CardValue.WILD4]: "Rút 4",
      };

      const colorName = colorNames[card.color] || card.color;
      const valueName = typeof card.value === "number" 
        ? card.value.toString() 
        : valueNames[card.value] || card.value;

      if (card.color === CardColor.WILD) {
        return valueName || "Đổi màu";
      }
      return `${colorName} ${valueName}`;
    };

    const addLog = (message: string) => {
      setGameLogs((prev) => [
        ...prev,
        {
          id: `${Date.now()}-${Math.random()}`,
          timestamp: new Date(),
          message,
        },
      ]);
    };

    const handleGameStateUpdated = (state: GameState) => {
      console.log("[Game] GAME_STATE_UPDATED received:", state);
      
      const prevState = previousGameStateRef.current;
      
      // Detect direction change - show subtle indicator for 0.5s
      if (prevState && prevState.direction !== state.direction) {
        setShowDirectionIndicator(true);
        setTimeout(() => setShowDirectionIndicator(false), 500);
        previousDirectionRef.current = state.direction;
      }
      
      // Detect combo stack increase (pendingDraw increases)
      if (prevState && prevState.pendingDraw < state.pendingDraw && state.pendingDraw > 0) {
        triggerHaptic("medium"); // Combo stack increase
      }
      
      // Detect draw stack resolution (pendingDraw goes from >0 to 0)
      if (prevState && prevState.pendingDraw > 0 && state.pendingDraw === 0) {
        triggerHaptic("heavy"); // Combo resolution (penalty draw)
        // Stack was resolved - find which player drew cards
        const targetPlayer = state.players.find((player) => {
          const prevPlayer = prevState.players.find((p) => p.id === player.id);
          return prevPlayer && player.cardsCount > prevPlayer.cardsCount;
        });

        if (targetPlayer && drawButtonRef.current) {
          const prevPlayer = prevState.players.find((p) => p.id === targetPlayer.id);
          const cardsDrawn = targetPlayer.cardsCount - (prevPlayer?.cardsCount || 0);
          
          // Trigger draw storm animation with staggered timing
          const drawButtonRect = drawButtonRef.current.getBoundingClientRect();
          
          // Create multiple flying cards for draw storm with delays
          for (let i = 0; i < cardsDrawn; i++) {
            setTimeout(() => {
              // Create a dummy card for animation (we don't know the actual card)
              const dummyCard: Card = {
                color: CardColor.RED,
                value: 0,
              };
              
              // Destination: player hand area (bottom center with random offset)
              const handAreaRect = {
                left: window.innerWidth / 2 + (Math.random() - 0.5) * 200,
                top: window.innerHeight - 200 + (Math.random() - 0.5) * 100,
                width: 100,
                height: 150,
              };
              
              setDrawStormCards((prev) => [
                ...prev,
                {
                  card: dummyCard,
                  from: drawButtonRect,
                  to: handAreaRect as DOMRect,
                  faceDown: true, // Draw storm cards are face down
                  rotation: (Math.random() - 0.5) * 30, // Random rotation between -15 and 15 degrees
                  duration: 0.15, // Faster animation for draw storm
                },
              ]);
            }, i * 50); // 50ms delay between each card
          }
        }
      }
      
      // Log player actions by comparing previous and current state
      if (prevState && prevState.status === GameStatus.PLAYING && state.status === GameStatus.PLAYING) {
        // Check if a card was played (discard pile increased)
        if (state.discardPile.length > prevState.discardPile.length) {
          const playedCard = state.discardPile[state.discardPile.length - 1];
          const playerId = prevState.currentTurnPlayerId;
          const player = prevState.players.find((p) => p.id === playerId);
          const playerName = player?.name || "Người chơi";
          
          // Check if color changed (wild card was played)
          const colorChanged = state.currentColor !== prevState.currentColor;
          const cardDescription = formatCard(playedCard);
          
          if (colorChanged && playedCard.color === CardColor.WILD) {
            addLog(`${playerName} đã đánh ${cardDescription} và chọn màu ${state.currentColor}`);
          } else {
            addLog(`${playerName} đã đánh ${cardDescription}`);
          }
          
          // ANIMATION: Detect +2/+4 card play and trigger animation sequence
          const isPenaltyCard = playedCard.value === CardValue.DRAW2 || playedCard.value === CardValue.WILD4;
          if (isPenaltyCard && state.pendingDraw > 0) {
            // Find the affected player (next player in turn order)
            const currentPlayerIndex = state.players.findIndex(p => p.id === playerId);
            const nextPlayerIndex = (currentPlayerIndex + 1) % state.players.length;
            const affectedPlayer = state.players[nextPlayerIndex];
            
            if (affectedPlayer) {
              // Start animation sequence
              setPenaltyAnimationState({
                phase: "cardImpact",
                affectedPlayerId: affectedPlayer.id,
                penaltyType: playedCard.value === CardValue.WILD4 ? "+4" : "+2",
                count: state.pendingDraw,
              });
              
              // Step 1: Card Impact (200ms)
              setTimeout(() => {
                setPenaltyAnimationState(prev => ({ ...prev, phase: "targetIndication" }));
              }, prefersReducedMotion ? 100 : 200);
              
              // Step 2: Target Indication (200ms)
              setTimeout(() => {
                setPenaltyAnimationState(prev => ({ ...prev, phase: "drawCounter" }));
              }, prefersReducedMotion ? 200 : 400);
              
              // Step 3: Draw Counter (150ms)
              setTimeout(() => {
                setPenaltyAnimationState(prev => ({ ...prev, phase: "cardDraw" }));
              }, prefersReducedMotion ? 300 : 550);
              
              // Step 4: Card Draw (300ms) - handled by draw resolution detection below
              // Step 5: Reset - will be triggered when pendingDraw resolves
            }
          } else if (prevState.pendingDraw > 0 && state.pendingDraw === 0) {
            // Draw stack resolved - reset animation
            setPenaltyAnimationState({
              phase: "reset",
              affectedPlayerId: null,
              penaltyType: null,
              count: 0,
            });
            setTimeout(() => {
              setPenaltyAnimationState({
                phase: "idle",
                affectedPlayerId: null,
                penaltyType: null,
                count: 0,
              });
            }, prefersReducedMotion ? 100 : 300);
          } else if (prevState.pendingDraw < state.pendingDraw && state.pendingDraw > 0) {
            // Draw chain escalation - update counter only
            setPenaltyAnimationState(prev => ({
              ...prev,
              count: state.pendingDraw,
              phase: prev.phase === "idle" ? "drawCounter" : prev.phase, // Keep current phase or show counter
            }));
          }
        }
        
        // Check if a player drew cards (hand size increased)
        state.players.forEach((player) => {
          const prevPlayer = prevState.players.find((p) => p.id === player.id);
          if (prevPlayer && player.cardsCount > prevPlayer.cardsCount) {
            const cardsDrawn = player.cardsCount - prevPlayer.cardsCount;
            
            // Check if this is due to pending draw (+2 or +4 card effect)
            // If prevState had pendingDraw and player drew exactly that many cards, it's from card effect
            const isFromCardEffect = prevState.pendingDraw > 0 && cardsDrawn === prevState.pendingDraw;
            
            // Check if this is a penalty (player drew cards but it's not their turn)
            // Penalty happens when:
            // 1. Player drew cards but it's not their turn AND it's not from card effect (penalty for not calling UNO)
            // 2. Player had 1 card before but didn't call UNO
            const isPenalty = 
              (!isFromCardEffect && state.currentTurnPlayerId !== player.id) || 
              (prevPlayer.cardsCount === 1 && !prevPlayer.hasSaidUNO);
            
            if (isPenalty) {
              addLog(`⚠️ ${player.name} bị phạt rút ${cardsDrawn} lá bài (quên gọi UNO)`);
            } else if (isFromCardEffect) {
              // Show message for card effect draws
              if (cardsDrawn === 2) {
                addLog(`📥 ${player.name} đã rút ${cardsDrawn} lá bài (bị đánh +2)`);
              } else if (cardsDrawn === 4) {
                addLog(`📥 ${player.name} đã rút ${cardsDrawn} lá bài (bị đánh +4)`);
              } else {
                addLog(`📥 ${player.name} đã rút ${cardsDrawn} lá bài`);
              }
              
              // Trigger penalty animation and flying cards for Draw +2/+4 (if it's the current user)
              if (player.id === userId && (cardsDrawn === 2 || cardsDrawn === 4)) {
                setPenaltyState({
                  active: true,
                  type: cardsDrawn === 2 ? "draw2" : "draw4",
                  cardsCount: cardsDrawn,
                });
                triggerHaptic("heavy");
                
                // Trigger flying cards animation
                if (drawButtonRef.current) {
                  const drawButtonRect = drawButtonRef.current.getBoundingClientRect();
                  
                  for (let i = 0; i < cardsDrawn; i++) {
                    setTimeout(() => {
                      const dummyCard: Card = {
                        color: CardColor.RED,
                        value: 0,
                      };
                      
                      const handAreaRect = {
                        left: window.innerWidth / 2 + (Math.random() - 0.5) * 200,
                        top: window.innerHeight - 200 + (Math.random() - 0.5) * 100,
                        width: 100,
                        height: 150,
                      };
                      
                      setDrawStormCards((prev) => [
                        ...prev,
                        {
                          card: dummyCard,
                          from: drawButtonRect,
                          to: handAreaRect as DOMRect,
                          faceDown: true,
                          rotation: (Math.random() - 0.5) * 30,
                          duration: 0.2,
                        },
                      ]);
                    }, i * 60); // Staggered timing for dramatic effect
                  }
                }
                
                // Clear penalty animation after 1.5 seconds
                setTimeout(() => {
                  setPenaltyState({ active: false, type: null, cardsCount: 0 });
                }, 1500);
              }
            } else {
              addLog(`${player.name} đã rút ${cardsDrawn} lá bài`);
            }
          }
        });
        
        // Check if turn changed
        if (state.currentTurnPlayerId !== prevState.currentTurnPlayerId) {
          const newTurnPlayer = state.players.find((p) => p.id === state.currentTurnPlayerId);
          if (newTurnPlayer) {
            addLog(`Đến lượt của ${newTurnPlayer.name}`);
          }
        }
      }
      
      // Initialize logs when game starts
      if (!prevState && state.status === GameStatus.PLAYING) {
        addLog("Trò chơi bắt đầu!");
        const firstPlayer = state.players.find((p) => p.id === state.currentTurnPlayerId);
        if (firstPlayer) {
          addLog(`Đến lượt của ${firstPlayer.name}`);
        }
      }
      
      // Detect hand size transitions for UNO UX
      if (userId) {
        const prevHandSize = previousHandSizeRef.current ?? (prevState?.hands[userId]?.length ?? state.hands[userId]?.length ?? 0);
        const currentHandSize = state.hands[userId]?.length ?? 0;
        const myPlayer = state.players.find((p) => p.id === userId);
        const isMyTurn = state.currentTurnPlayerId === userId;
        const hasSaidUNO = myPlayer?.hasSaidUNO ?? false;
        
        // Detect UNO penalty (hand size increased but not from card effect)
        // Only check penalty if we have previous state to compare
        if (prevState) {
          const prevPlayer = prevState.players.find((p) => p.id === userId);
          const cardsDrawn = currentHandSize - prevHandSize;
          const isFromCardEffect = prevState.pendingDraw > 0 && cardsDrawn === prevState.pendingDraw;
          const isPenalty = 
            cardsDrawn > 0 &&
            ((!isFromCardEffect && state.currentTurnPlayerId !== userId) ||
             (prevPlayer?.cardsCount === 1 && !prevPlayer.hasSaidUNO));
        
          if (isPenalty && cardsDrawn === 2) {
            // UNO penalty detected - show penalty visualization
            setUnoUXState((prev) => ({
              ...prev,
              penaltyActive: true,
              needsReminder: false,
              urgent: false,
            }));
            
            // Show dramatic penalty animation
            setPenaltyState({
              active: true,
              type: "missedUno",
              cardsCount: cardsDrawn,
            });
            
            triggerHaptic("error");
            
            // Trigger draw storm animation for UNO penalty
            if (drawButtonRef.current) {
              const drawButtonRect = drawButtonRef.current.getBoundingClientRect();
              
              // Create multiple flying cards for penalty draw with delays
              for (let i = 0; i < cardsDrawn; i++) {
                setTimeout(() => {
                  const dummyCard: Card = {
                    color: CardColor.RED,
                    value: 0,
                  };
                  
                  // Destination: player hand area (bottom center with random offset)
                  const handAreaRect = {
                    left: window.innerWidth / 2 + (Math.random() - 0.5) * 200,
                    top: window.innerHeight - 200 + (Math.random() - 0.5) * 100,
                    width: 100,
                    height: 150,
                  };
                  
                  setDrawStormCards((prev) => [
                    ...prev,
                    {
                      card: dummyCard,
                      from: drawButtonRect,
                      to: handAreaRect as DOMRect,
                      faceDown: true,
                      rotation: (Math.random() - 0.5) * 30,
                      duration: 0.2,
                    },
                  ]);
                }, i * 60); // Staggered timing for dramatic effect
              }
            }
            
            // Clear penalty visualization after animation
            setTimeout(() => {
              setUnoUXState((prev) => ({
                ...prev,
                penaltyActive: false,
              }));
              setPenaltyState({ active: false, type: null, cardsCount: 0 });
            }, 2000);
          }
        }
        
        // Update UNO reminder state based on current hand size
        if (isMyTurn && !hasSaidUNO) {
          if (currentHandSize === 2) {
            setUnoUXState((prev) => ({
              ...prev,
              needsReminder: true,
              urgent: false,
            }));
          } else if (currentHandSize === 1) {
            // Player reached 1 card - show urgent reminder
            // Only show animation if they haven't called UNO yet
            const justReachedOne = prevHandSize !== 1;
            setUnoUXState((prev) => ({
              ...prev,
              needsReminder: false,
              urgent: true,
              // Only trigger animation if they just reached 1 card (transition from 2 to 1)
              successActive: justReachedOne && !hasSaidUNO,
            }));
            
            if (justReachedOne && !hasSaidUNO) {
              triggerHaptic("medium");
              // Clear animation after showing
              setTimeout(() => {
                setUnoUXState((prev) => ({
                  ...prev,
                  successActive: false,
                }));
              }, 2000);
            }
          } else {
            setUnoUXState((prev) => ({
              ...prev,
              needsReminder: false,
              urgent: false,
            }));
          }
        } else {
          // Not player's turn or already called UNO
          setUnoUXState((prev) => ({
            ...prev,
            needsReminder: false,
            urgent: false,
          }));
        }
        
        previousHandSizeRef.current = currentHandSize;
      }
      
      previousGameStateRef.current = state;
      setGameState(state);
      setError("");
      
      // Check if user is a spectator (no hand in game state)
      if (userId) {
        const hasHand = state.hands[userId] && state.hands[userId].length > 0;
        setIsSpectator(!hasHand);
        console.log("[Game] User has hand:", hasHand, "hand length:", state.hands[userId]?.length);
      } else {
        setIsSpectator(true);
      }
      
      // Reset canPlayDrawnCard when state updates (unless it's still our turn and we just drew)
      // This will be set again by CAN_PLAY_DRAWN_CARD if applicable
      if (userId && state.currentTurnPlayerId !== userId) {
        setCanPlayDrawnCard(false);
      }
    };

    const handleInvalidMove = (data: { error: string }) => {
      setError(data.error);
      triggerHaptic("error"); // Invalid move feedback
      // Reset drag state on invalid move (card will snap back)
      setDraggedCardIndex(null);
      setIsOverDiscardPile(false);
      setJustDragged(false);
      // Clear flying card animation on invalid move
      setFlyingCard(null);
    };

    const handleGameOver = (data: { winnerId: string; winnerName?: string }) => {
      const winnerName = data.winnerName || "Người chơi";
      addLog(`🎉 ${winnerName} đã thắng!`);
      triggerHaptic("success"); // Game end feedback
      // Xóa localStorage khi game over
      localStorage.removeItem("uno_room");
      // Show end game screen instead of alert
      setGameOverState({
        winnerId: data.winnerId,
        winnerName: data.winnerName,
      });
    };

    const handleError = (data: { message: string }) => {
      // Handle PASSIVE_SESSION error silently (UI already shows banner)
      if (data.message === "PASSIVE_SESSION") {
        console.log("[Game] Action rejected: This tab is in passive mode");
        return;
      }
      setError(data.message);
      // Nếu lỗi là room not found hoặc không thể join, xóa localStorage
      if (data.message.includes("Room") && (data.message.includes("not found") || data.message.includes("not in"))) {
        localStorage.removeItem("uno_room");
      }
    };

    const handleSessionRoleUpdated = (data: { role: "active" | "passive" }) => {
      console.log(`[Game] Session role updated: ${data.role}`);
      setSessionRole(data.role);
    };

    const handleCanPlayDrawnCard = () => {
      setCanPlayDrawnCard(true);
      // The drawn card is the last card in the hand
    };

    const handleUNOCalled = (data: { playerId: string; playerName?: string }) => {
      // Show notification
      const playerName = data.playerName || "Người chơi";
      console.log(`${playerName} called UNO!`);
      addLog(`${playerName} đã gọi UNO!`);
      
      // Show success feedback if this player called UNO
      if (data.playerId === userId) {
        setUnoUXState((prev) => ({
          ...prev,
          successActive: true,
          needsReminder: false,
          urgent: false,
        }));
        triggerHaptic("success");
        
        // Clear success feedback after animation
        setTimeout(() => {
          setUnoUXState((prev) => ({
            ...prev,
            successActive: false,
          }));
        }, 2000);
      } else {
        // Show animation for other players' UNO calls
        setOtherPlayerUnoCall({
          playerId: data.playerId,
          playerName,
          active: true,
        });
        
        // Clear after animation
        setTimeout(() => {
          setOtherPlayerUnoCall(null);
        }, 2000);
      }
    };

    const handleGamePaused = (data: { message: string }) => {
      alert(data.message);
    };

    // Setup listeners FIRST before requesting state
    socket.onGameStateUpdated(handleGameStateUpdated);
    socket.onInvalidMove(handleInvalidMove);
    socket.onGameOver(handleGameOver);
    socket.onError(handleError);
    socket.onCanPlayDrawnCard(handleCanPlayDrawnCard);
    socket.onUNOCalled(handleUNOCalled);
    socket.onGamePaused(handleGamePaused);
    socket.onSessionRoleUpdated(handleSessionRoleUpdated);

    // Request game state and logs when component mounts
    // This ensures we get the latest state even if we missed the initial GAME_STATE_UPDATED
    const requestTimeout = setTimeout(() => {
      if (roomId) {
        console.log("[Game] Requesting room state for:", roomId);
        socket.getRoomState(roomId);
        socket.getGameLogs(roomId);
      }
    }, 100);

    // Handle game logs loaded from server
    const handleGameLogsLoaded = (data: {
      roomId: string;
      logs: Array<{ id: string; message: string; timestamp: number }>;
    }) => {
      if (data.roomId === roomId) {
        console.log("[Game] Game logs loaded:", data.logs.length);
        // Convert server logs to client format
        const serverLogs = data.logs.map(log => ({
          id: log.id,
          timestamp: new Date(log.timestamp),
          message: log.message,
        }));
        // Replace existing logs with server logs (server is source of truth)
        setGameLogs(serverLogs);
      }
    };

    // Handle new game log from server
    const handleGameLog = (data: {
      id: string;
      roomId: string;
      message: string;
      timestamp: number;
    }) => {
      if (data.roomId === roomId) {
        console.log("[Game] New game log:", data.message);
        // Add new log from server
        setGameLogs((prev) => [
          ...prev,
          {
            id: data.id,
            timestamp: new Date(data.timestamp),
            message: data.message,
          },
        ]);
      }
    };

    socket.onGameLogsLoaded(handleGameLogsLoaded);
    socket.onGameLog(handleGameLog);

    return () => {
      clearTimeout(requestTimeout);
      socket.off("GAME_STATE_UPDATED", handleGameStateUpdated);
      socket.off("INVALID_MOVE", handleInvalidMove);
      socket.off("GAME_OVER", handleGameOver);
      socket.off("ERROR", handleError);
      socket.off("CAN_PLAY_DRAWN_CARD", handleCanPlayDrawnCard);
      socket.off("UNO_CALLED", handleUNOCalled);
      socket.off("GAME_PAUSED", handleGamePaused);
      socket.off("SESSION_ROLE_UPDATED", handleSessionRoleUpdated);
      socket.off("GAME_LOGS_LOADED", handleGameLogsLoaded);
      socket.off("GAME_LOG", handleGameLog);
    };
  }, [roomId, navigate, socket, userId]);

  // Derive draw stack UI state from game state (UI-only, no server logic)
  const drawStackUI = useMemo<DrawStackUI | null>(() => {
    if (!gameState || gameState.pendingDraw === 0) {
      return null;
    }

    const topCard = gameState.discardPile[gameState.discardPile.length - 1];
    const isDraw2 = topCard.value === CardValue.DRAW2;
    const isWild4 = topCard.value === CardValue.WILD4;

    // Determine stack type based on top card
    if (isDraw2 || isWild4) {
      return {
        count: gameState.pendingDraw,
        type: isWild4 ? "+4" : "+2",
        active: true,
      };
    }

    return null;
  }, [gameState?.pendingDraw, gameState?.discardPile]);

  // UI State Map - Defines visual priority for each game state
  // Each state has ONE primary focus, others are visually downgraded
  type UIFocusLevel = "primary" | "secondary" | "dimmed";
  type UIState = {
    state: 
      | "idle"                    // Waiting for turn
      | "playerTurn"              // Normal play - player's turn
      | "drawnCardAvailable"      // Just drew a card, can play it
      | "penaltyActive"           // Draw +2/+4/chain penalty active
      | "unoWarning"              // Player has 2 cards, needs UNO reminder
      | "forcedDraw"              // Must draw (no playable cards)
      | "pendingDraw";            // Pending draw active (not player's turn)
    opponentsZone: UIFocusLevel;
    centerTable: UIFocusLevel;
    discardPile: UIFocusLevel;
    drawDeck: UIFocusLevel;
    playerHand: UIFocusLevel;
    drawnCard?: UIFocusLevel;     // Only relevant when drawnCardAvailable
  };

  // Derive UI state from game state (no new backend state)
  const uiState = useMemo<UIState>(() => {
    if (!gameState || !userId) {
      return {
        state: "idle",
        opponentsZone: "secondary",
        centerTable: "secondary",
        discardPile: "secondary",
        drawDeck: "secondary",
        playerHand: "secondary", // Not dimmed when waiting
      };
    }

    const isMyTurn = gameState.currentTurnPlayerId === userId;
    const myPlayer = gameState.players.find((p) => p.id === userId);
    const myHand = (userId ? gameState.hands[userId] : undefined) || [];
    const hasTwoCards = myHand.length === 2;
    const needsUNOReminder = isMyTurn && hasTwoCards && !myPlayer?.hasSaidUNO;
    const isPenaltyActive = penaltyState.active && penaltyState.type !== null;
    const hasPlayableCards = myHand.some((card, idx) => {
      // Helper to check basic playability
      const topCard = gameState.discardPile[gameState.discardPile.length - 1];
      if (gameState.pendingDraw > 0) {
        return (card.value === CardValue.DRAW2 && topCard.value === CardValue.DRAW2) ||
               card.value === CardValue.WILD4;
      }
      if (card.color === CardColor.WILD && card.value === CardValue.WILD) return true;
      if (card.value === CardValue.WILD4) return true;
      if (card.color === gameState.currentColor) return true;
      if (topCard.color !== CardColor.WILD && card.value === topCard.value) return true;
      return false;
    });

    // Priority order: penalty > drawnCard > unoWarning > forcedDraw > playerTurn > idle
    // All elements are visible (no dimming)
    if (isPenaltyActive) {
      // PENALTY STATE: Penalty card is primary focus
      return {
        state: "penaltyActive",
        opponentsZone: "secondary",
        centerTable: "primary",      // Penalty card is here
        discardPile: "primary",      // Penalty card is here
        drawDeck: "secondary",
        playerHand: "secondary",
      };
    }

    if (canPlayDrawnCard && isMyTurn) {
      // DRAWN CARD AVAILABLE: Drawn card is primary focus
      return {
        state: "drawnCardAvailable",
        opponentsZone: "secondary",
        centerTable: "secondary",    // Discard pile visible but secondary
        discardPile: "secondary",
        drawDeck: "secondary",
        playerHand: "secondary",      // Hand visible, drawn card will be shown separately
        drawnCard: "primary",        // Drawn card is the focus
      };
    }

    if (needsUNOReminder) {
      // UNO WARNING: Player hand pulses, UNO button appears
      return {
        state: "unoWarning",
        opponentsZone: "secondary",
        centerTable: "secondary",
        discardPile: "secondary",
        drawDeck: "secondary",
        playerHand: "primary",       // Hand is focus (pulses)
      };
    }

    // Check if it's player's turn with pending draw - MUST CHECK FIRST before other isMyTurn conditions
    // When there's pending draw and it's player's turn, focus on discard pile (to see what to stack)
    // Draw deck should be visible but secondary (player can draw or stack)
    if (isMyTurn && gameState.pendingDraw > 0) {
      return {
        state: "forcedDraw",
        opponentsZone: "secondary",
        centerTable: "primary",      // Discard pile is primary focus (to see what to stack)
        discardPile: "primary",      // Discard pile is primary focus
        drawDeck: "secondary",       // Draw deck visible but secondary (can draw)
        playerHand: "secondary",     // Hand visible to see stackable cards
      };
    }

    if (isMyTurn && !hasPlayableCards && !canPlayDrawnCard && gameState.pendingDraw === 0) {
      // FORCED DRAW: Must draw, no playable cards (no pending draw)
      return {
        state: "forcedDraw",
        opponentsZone: "secondary",
        centerTable: "primary",      // Draw deck pulses
        discardPile: "secondary",
        drawDeck: "primary",         // Draw deck is focus
        playerHand: "secondary",     // Hand visible
      };
    }

    if (isMyTurn) {
      // PLAYER TURN: Normal play - hand is primary focus
      return {
        state: "playerTurn",
        opponentsZone: "secondary",
        centerTable: "secondary",    // Discard pile visible but secondary
        discardPile: "secondary",
        drawDeck: "secondary",
        playerHand: "primary",       // Hand is primary focus
      };
    }

    // Check if there's a pending draw (not player's turn)
    // When there's a pending draw, keep all elements visible
    if (gameState.pendingDraw > 0) {
      return {
        state: "pendingDraw",
        opponentsZone: "secondary",
        centerTable: "secondary",     // Draw notification visible but not overly emphasized
        discardPile: "secondary",     // Discard pile visible to show what was played
        drawDeck: "secondary",        // Draw deck visible
        playerHand: "secondary",       // Hand visible but not primary focus
      };
    }

    // IDLE: Waiting for turn
    return {
      state: "idle",
      opponentsZone: "secondary",    // Normal visibility
      centerTable: "secondary",      // Visible but calm
      discardPile: "secondary",
      drawDeck: "secondary",
      playerHand: "secondary",       // Hand visible, not dimmed
    };
  }, [gameState, userId, canPlayDrawnCard, penaltyState]);

  // Visual Priority Style Helpers
  // Returns style objects based on focus level (primary, secondary, dimmed)
  const getFocusStyles = (focusLevel: UIFocusLevel): React.CSSProperties => {
    switch (focusLevel) {
      case "primary":
        return {
          opacity: 1,
          filter: "none",
          transform: "scale(1)",
          transition: "opacity 0.3s ease, filter 0.3s ease, transform 0.3s ease",
        };
      case "secondary":
        return {
          opacity: 0.85,
          filter: "none",
          transform: "scale(1)",
          transition: "opacity 0.3s ease, filter 0.3s ease, transform 0.3s ease",
        };
      case "dimmed":
        // Casual Party: NO blur, use opacity and saturation instead
        const isCasualParty = typeof window !== "undefined" && 
          document.documentElement.getAttribute("data-theme") === "casual-party";
        return {
          opacity: 0.4,
          filter: isCasualParty 
            ? "brightness(0.85) saturate(0.7)" // No blur for Casual Party
            : "blur(2px) brightness(0.7)",
          transform: "scale(0.98)",
          transition: "opacity 0.3s ease, filter 0.3s ease, transform 0.3s ease",
        };
    }
  };

  // Get current theme for theme-aware styling
  const currentTheme = useMemo(() => {
    if (typeof window === "undefined") return "classic-dark";
    const root = document.documentElement;
    return root.getAttribute("data-theme") || "classic-dark";
  }, []);
  const isCasualParty = currentTheme === "casual-party";

  // Get zone-specific styles based on UI state
  const centerTableStyle = useMemo(() => {
    const baseStyle = getFocusStyles(uiState.centerTable);
    // Casual Party: flat pastel table with clear inner border/vignette to define edges
    if (isCasualParty) {
      return {
        ...baseStyle,
        // Flat pastel table background - clearly separates from page background
        background: "var(--table)",
        // Clear inner border and vignette to separate table from background
        // No blur inside table area - clean, flat appearance
        boxShadow: "inset 0 0 0 2px rgba(44, 36, 22, 0.15), inset 0 0 80px rgba(44, 36, 22, 0.05)",
        // Ensure no blur filters inside table area
        backdropFilter: "none",
        filter: "none",
      };
    }
    return baseStyle;
  }, [uiState.centerTable, isCasualParty]);
  const playerHandStyle = useMemo(() => getFocusStyles(uiState.playerHand), [uiState.playerHand]);
  const drawDeckStyle = useMemo(() => getFocusStyles(uiState.drawDeck), [uiState.drawDeck]);

  // Memoize card layout calculations - must be called before early return
  const myHand = (gameState && userId ? gameState.hands[userId] : undefined) || [];
  
  // Clean up refs for cards that are no longer in hand
  useEffect(() => {
    const currentCardIndices = new Set(myHand.map((_, index) => index));
    draggedCardRefs.current.forEach((_, index) => {
      if (!currentCardIndices.has(index)) {
        draggedCardRefs.current.delete(index);
      }
    });
    cardWrapperRefs.current.forEach((_, index) => {
      if (!currentCardIndices.has(index)) {
        cardWrapperRefs.current.delete(index);
      }
    });
  }, [myHand.length]);

  // Reset hand mode when hand size changes significantly (must be before early return)
  useEffect(() => {
    if (isMobile && myHand.length <= 5 && handUIMode !== "compact") {
      setHandUIMode("compact");
      setFocusedCardIndex(null);
    }
  }, [myHand.length, isMobile, handUIMode]);
  
  // Reset horizontal offset when hand mode changes or hand size changes
  useEffect(() => {
    if (handUIMode !== "compact") {
      setHorizontalOffset(0);
      horizontalOffsetRef.current = 0;
    }
  }, [handUIMode, myHand.length]);
  
  // Sort cards by color while preserving original indices
  const sortedCardsWithIndices = useMemo(() => {
    const cardsWithIndices = myHand.map((card, index) => ({ card, originalIndex: index }));
    
    // Define color order: red, blue, green, yellow, wild
    const colorOrder = [CardColor.RED, CardColor.BLUE, CardColor.GREEN, CardColor.YELLOW, CardColor.WILD];
    const colorOrderMap = new Map(colorOrder.map((color, idx) => [color, idx]));
    
    // Sort by color order, then by value
    return cardsWithIndices.sort((a, b) => {
      const colorOrderA = colorOrderMap.get(a.card.color) ?? 999;
      const colorOrderB = colorOrderMap.get(b.card.color) ?? 999;
      
      if (colorOrderA !== colorOrderB) {
        return colorOrderA - colorOrderB;
      }
      
      // If same color, sort by value
      const valueA = typeof a.card.value === "number" ? a.card.value : 999;
      const valueB = typeof b.card.value === "number" ? b.card.value : 999;
      return valueA - valueB;
    });
  }, [myHand]);
  
  // Calculate layout for sorted cards (single continuous fan)
  // Supports 3-layer hand system on mobile: compact, expanded, focused
  const cardLayout = useMemo(() => {
    const totalCards = sortedCardsWithIndices.length;
    if (totalCards === 0) return new Map();
    
    const layoutMap = new Map<number, { 
      rotation: number; 
      translateX: number; 
      zIndex: number;
      translateY?: number;
      scale?: number;
      opacity?: number;
    }>();
    
    // Desktop: always use normal fan layout
    if (!isMobile) {
      const maxRotation = 15; // degrees
      const maxSpread = Math.min(totalCards * 8, 200); // pixels
      const baseRotation = -maxRotation / 2;
      const rotationStep = maxRotation / Math.max(totalCards - 1, 1);
      
      sortedCardsWithIndices.forEach(({ originalIndex }, index) => {
        const position = index / Math.max(totalCards - 1, 1);
        const rotation = baseRotation + rotationStep * index;
        const translateX = (position - 0.5) * maxSpread;
        
        layoutMap.set(originalIndex, {
          rotation,
          translateX,
          zIndex: index,
        });
      });
      return layoutMap;
    }
    
    // Mobile: 3-layer hand system
    const LARGE_HAND_THRESHOLD = 10;
    const isLargeHand = totalCards > LARGE_HAND_THRESHOLD;
    
    if (handUIMode === "compact") {
      // Compact mode: compressed fan for large hands
      const maxRotation = isLargeHand ? 12 : 15; // Slightly less rotation for large hands
      const maxSpread = isLargeHand 
        ? Math.min(totalCards * 5, 180) // More compressed spread
        : Math.min(totalCards * 8, 200);
      const baseRotation = -maxRotation / 2;
      const rotationStep = maxRotation / Math.max(totalCards - 1, 1);
      
      sortedCardsWithIndices.forEach(({ originalIndex }, index) => {
        const position = index / Math.max(totalCards - 1, 1);
        const rotation = baseRotation + rotationStep * index;
        // Apply horizontal offset for browsing in compact mode
        const translateX = (position - 0.5) * maxSpread + horizontalOffset;
        
        layoutMap.set(originalIndex, {
          rotation,
          translateX,
          zIndex: index,
        });
      });
    } else if (handUIMode === "expanded") {
      // Expanded mode: wider arc, cards more spread out
      const maxRotation = 20; // More rotation for better visibility
      const maxSpread = Math.min(totalCards * 12, Math.min(window.innerWidth * 0.9, 400)); // Wider spread
      const baseRotation = -maxRotation / 2;
      const rotationStep = maxRotation / Math.max(totalCards - 1, 1);
      
      sortedCardsWithIndices.forEach(({ originalIndex }, index) => {
        const position = index / Math.max(totalCards - 1, 1);
        const rotation = baseRotation + rotationStep * index;
        const translateX = (position - 0.5) * maxSpread;
        
        layoutMap.set(originalIndex, {
          rotation,
          translateX,
          zIndex: index,
          translateY: -60, // Lift cards up
        });
      });
    } else if (handUIMode === "focused" && focusedCardIndex !== null) {
      // Focused mode: selected card scales up, others dim
      const maxRotation = 15;
      const maxSpread = Math.min(totalCards * 8, 200);
      const baseRotation = -maxRotation / 2;
      const rotationStep = maxRotation / Math.max(totalCards - 1, 1);
      
      sortedCardsWithIndices.forEach(({ originalIndex }, index) => {
        const position = index / Math.max(totalCards - 1, 1);
        const rotation = baseRotation + rotationStep * index;
        const translateX = (position - 0.5) * maxSpread;
        const isFocused = originalIndex === focusedCardIndex;
        
        layoutMap.set(originalIndex, {
          rotation: isFocused ? 0 : rotation, // Focused card is straight
          translateX,
          zIndex: isFocused ? 2000 : index,
          translateY: isFocused ? -80 : -60, // Focused card lifts more
          scale: isFocused ? 1.3 : 1,
          opacity: isFocused ? 1 : 0.4, // Dim non-focused cards
        });
      });
    }
    
    return layoutMap;
  }, [sortedCardsWithIndices, isMobile, handUIMode, focusedCardIndex, horizontalOffset]);

  if (!gameState || !userId) {
    return (
      <div style={styles.loading}>
        <div>Loading game...</div>
      </div>
    );
  }

  const isMyTurn = gameState.currentTurnPlayerId === userId;
  const topCard = gameState.discardPile[gameState.discardPile.length - 1];
  const myPlayer = gameState.players.find((p) => p.id === userId);
  const needsUNO = myHand.length === 1 && !myPlayer?.hasSaidUNO;

  // Safety check: if no top card, cards cannot be played
  if (!topCard) {
    console.warn("[Game] No top card in discard pile");
  }

  // Helper function for basic card playability check (without WILD4 restriction)
  const canPlayCardBasic = (card: Card): boolean => {
    // Safety check: if no top card, cards cannot be played
    if (!topCard) {
      return false;
    }
    // If there's a pending draw, only matching draw cards can be played
    if (gameState.pendingDraw > 0) {
      if (gameState.pendingDraw === 2) {
        // Can play draw2 of any color (if topCard is DRAW2) or wild4 to stack
        return (card.value === CardValue.DRAW2 && topCard.value === CardValue.DRAW2) ||
               card.value === CardValue.WILD4;
      }
      if (gameState.pendingDraw >= 4) {
        // Can play draw2 of any color (if topCard is DRAW2) or wild4 to stack
        // This allows continuing to stack DRAW2 when pendingDraw is 4, 6, 8, etc.
        return (card.value === CardValue.DRAW2 && topCard.value === CardValue.DRAW2) ||
               card.value === CardValue.WILD4;
      }
      return false;
    }

    // Regular wild card (not wild4) is always playable
    if (card.color === CardColor.WILD && card.value === CardValue.WILD) {
      return true;
    }

    // Wild Draw 4 is potentially playable (will be checked separately in canPlayCard)
    if (card.value === CardValue.WILD4) {
      return true;
    }

    // Same color
    if (card.color === gameState.currentColor) {
      return true;
    }

    // Same value (and top card is not wild)
    if (topCard.color !== CardColor.WILD && card.value === topCard.value) {
      return true;
    }

    return false;
  };

  // Helper function to check if a card can be played (with WILD4 restriction)
  // Note: This function does NOT check isMyTurn - that's checked separately in isDisabled and handleCardClick
  const canPlayCard = (card: Card, cardIndex: number): boolean => {
    // Wild Draw 4: can only be played if no other playable card exists
    // Exception: can always play wild4 when there's pendingDraw >= 4 to stack
    if (card.value === CardValue.WILD4 && gameState.pendingDraw < 4) {
      // Check if player has any other playable card (excluding other WILD4 cards)
      // If player only has WILD4 cards, they can still play WILD4
      const hasOtherPlayableCard = myHand.some((handCard, index) => {
        if (index === cardIndex) return false; // Skip the wild4 card itself
        if (handCard.value === CardValue.WILD4) return false; // Skip other WILD4 cards
        return canPlayCardBasic(handCard);
      });
      // Can only play WILD4 if no other playable card exists
      if (hasOtherPlayableCard) {
        return false;
      }
    }

    // Check basic playability
    return canPlayCardBasic(card);
  };

  // Hand UI mode handlers (mobile only)
  const handleHandExpand = () => {
    if (!isMobile) return;
    if (handUIMode === "compact") {
      setHandUIMode("expanded");
      triggerHaptic("light");
    }
  };

  const handleHandCollapse = () => {
    if (!isMobile) return;
    setHandUIMode("compact");
    setFocusedCardIndex(null);
    triggerHaptic("light");
  };

  const handleCardFocus = (cardIndex: number) => {
    if (!isMobile) return;
    if (handUIMode === "expanded") {
      setHandUIMode("focused");
      setFocusedCardIndex(cardIndex);
      triggerHaptic("light");
    }
  };

  const handleCardUnfocus = () => {
    if (!isMobile) return;
    if (handUIMode === "focused") {
      setHandUIMode("expanded");
      setFocusedCardIndex(null);
      triggerHaptic("light");
    }
  };

  /**
   * Card-level swipe handlers for immediate horizontal swiping
   * 
   * UX Design:
   * - Users can swipe left/right directly on cards to browse their hand
   * - No need to swipe up first - horizontal swipe works immediately
   * - Tap plays a card (detected when movement < TAP_THRESHOLD)
   * - Swipe follows finger smoothly with visual feedback
   * - Cards snap to nearest position on release
   * - Vertical scrolling is preserved (doesn't conflict with horizontal swipe)
   * 
   * Gesture Detection:
   * - Early horizontal intent detection (HORIZONTAL_THRESHOLD = 8px)
   * - Horizontal intent: |deltaX| > |deltaY| and exceeds threshold
   * - Vertical intent: |deltaY| > |deltaX| * 1.5 and upward
   * - Tap: movement < TAP_THRESHOLD (5px) and time < TAP_TIME_THRESHOLD (200ms)
   * 
   * Visual Feedback:
   * - Active swipe card scales to 1.1x and lifts slightly
   * - Smooth finger tracking via transform on hand container
   * - Snap to nearest card position on release
   */
  const handleCardTouchStart = (e: React.TouchEvent, cardIndex: number) => {
    if (!isMobile) return;
    
    e.stopPropagation(); // Prevent hand container handler
    
    const touch = e.touches[0];
    gestureStartX.current = touch.clientX;
    gestureStartY.current = touch.clientY;
    gestureIntentRef.current = "undecided";
    setCurrentGestureIntent("undecided");
    swipeStartTimeRef.current = Date.now();
    
    // Initialize swipe state
    setSwipingCardIndex(cardIndex);
    swipeStartOffsetRef.current = horizontalOffset;
    horizontalOffsetRef.current = horizontalOffset;
    
    // Prevent default to avoid scrolling
    e.preventDefault();
  };

  const handleCardTouchMove = (e: React.TouchEvent, cardIndex: number) => {
    if (!isMobile || gestureStartX.current === null || gestureStartY.current === null) return;
    if (swipingCardIndex !== cardIndex) return;
    
    e.stopPropagation(); // Prevent hand container handler
    
    const touch = e.touches[0];
    const deltaX = touch.clientX - gestureStartX.current;
    const deltaY = touch.clientY - gestureStartY.current;
    const absDeltaX = Math.abs(deltaX);
    const absDeltaY = Math.abs(deltaY);
    
    // Early horizontal intent detection (before threshold)
    if (gestureIntentRef.current === "undecided") {
      // Horizontal intent: prioritize horizontal movement early
      if (absDeltaX > absDeltaY && absDeltaX > HORIZONTAL_THRESHOLD) {
        gestureIntentRef.current = "horizontal";
        setCurrentGestureIntent("horizontal"); // Update state for visual feedback
        e.preventDefault(); // Prevent scrolling once horizontal intent is detected
      }
      // Vertical intent: only if clearly vertical and upward
      else if (absDeltaY > absDeltaX * 1.5 && deltaY < -VERTICAL_THRESHOLD) {
        gestureIntentRef.current = "vertical";
        setCurrentGestureIntent("vertical");
      }
      // Very small movement might be a tap
      else if (absDeltaX < TAP_THRESHOLD && absDeltaY < TAP_THRESHOLD) {
        gestureIntentRef.current = "tap";
        setCurrentGestureIntent("tap");
      }
    }
    
    // Handle horizontal swipe - smooth finger tracking
    if (gestureIntentRef.current === "horizontal" && handUIMode === "compact") {
      e.preventDefault(); // Prevent scrolling
      
      // Calculate new offset with smooth tracking
      const baseOffset = swipeStartOffsetRef.current;
      const newOffset = baseOffset + deltaX;
      
      // Clamp to reasonable bounds
      const cardSpacing = 35;
      const maxOffset = Math.max(0, (sortedCardsWithIndices.length - 1) * cardSpacing * 0.5);
      const clampedOffset = Math.max(-maxOffset, Math.min(maxOffset, newOffset));
      
      // Update swipe offset for visual feedback
      setSwipeOffset(clampedOffset - horizontalOffset);
      
      // Apply transform directly for smooth following
      if (handContainerRef.current) {
        handContainerRef.current.style.transform = `translateX(${clampedOffset}px)`;
      }
    }
  };

  const handleCardTouchEnd = (e: React.TouchEvent, cardIndex: number) => {
    if (!isMobile || gestureStartX.current === null || gestureStartY.current === null) return;
    if (swipingCardIndex !== cardIndex) return;
    
    e.stopPropagation(); // Prevent hand container handler
    
    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - gestureStartX.current;
    const deltaY = touch.clientY - gestureStartY.current;
    const absDeltaX = Math.abs(deltaX);
    const absDeltaY = Math.abs(deltaY);
    const swipeTime = swipeStartTimeRef.current ? Date.now() - swipeStartTimeRef.current : 0;
    
    // Final gesture intent determination
    if (gestureIntentRef.current === "undecided") {
      if (absDeltaX > absDeltaY && absDeltaX > HORIZONTAL_THRESHOLD) {
        gestureIntentRef.current = "horizontal";
        setCurrentGestureIntent("horizontal");
      } else if (absDeltaY > absDeltaX && deltaY < -VERTICAL_THRESHOLD) {
        gestureIntentRef.current = "vertical";
        setCurrentGestureIntent("vertical");
      } else if (absDeltaX < TAP_THRESHOLD && absDeltaY < TAP_THRESHOLD && swipeTime < TAP_TIME_THRESHOLD) {
        gestureIntentRef.current = "tap";
        setCurrentGestureIntent("tap");
      }
    }
    
    // Handle horizontal swipe end - snap to nearest card
    if (gestureIntentRef.current === "horizontal" && handUIMode === "compact") {
      const cardSpacing = 35;
      const currentOffset = swipeStartOffsetRef.current + deltaX;
      const snapOffset = Math.round(currentOffset / cardSpacing) * cardSpacing;
      
      // Clamp to reasonable bounds
      const maxOffset = Math.max(0, (sortedCardsWithIndices.length - 1) * cardSpacing * 0.5);
      const clampedOffset = Math.max(-maxOffset, Math.min(maxOffset, snapOffset));
      
      setHorizontalOffset(clampedOffset);
      horizontalOffsetRef.current = clampedOffset;
      setSwipeOffset(0);
      
      // Reset transform (will be handled by state update)
      if (handContainerRef.current) {
        handContainerRef.current.style.transform = "";
      }
    }
    // Handle tap - play card
    else if (gestureIntentRef.current === "tap") {
      console.log("[handleCardTouchEnd] Tap detected, calling handleCardClick", { cardIndex });
      // Small delay to ensure swipe state is cleared
      setTimeout(() => {
        handleCardClick(cardIndex);
      }, 0);
    } else {
      console.log("[handleCardTouchEnd] Not a tap, gesture intent:", gestureIntentRef.current, { cardIndex, absDeltaX, absDeltaY, swipeTime });
    }
    
    // Reset swipe state
    setSwipingCardIndex(null);
    setSwipeOffset(0);
    gestureIntentRef.current = "undecided";
    setCurrentGestureIntent("undecided");
    gestureStartX.current = null;
    gestureStartY.current = null;
    swipeStartTimeRef.current = null;
    
    // Reset transform if it was set
    if (handContainerRef.current) {
      handContainerRef.current.style.transform = "";
    }
  };

  // Hand container swipe handlers (for vertical swipe and background area)
  const handleHandTouchStart = (e: React.TouchEvent) => {
    if (!isMobile) return;
    
    // Check if touch target is a card (let card handle its own swipe)
    const target = e.target as HTMLElement;
    if (target.closest('[data-card-wrapper]')) {
      return; // Let card handle the gesture
    }
    
    const touch = e.touches[0];
    gestureStartX.current = touch.clientX;
    gestureStartY.current = touch.clientY;
    handSwipeStartY.current = touch.clientY;
    gestureIntentRef.current = "undecided";
    
    // Initialize horizontal offset ref from current state
    horizontalOffsetRef.current = horizontalOffset;
  };

  const handleHandTouchMove = (e: React.TouchEvent) => {
    if (!isMobile || gestureStartX.current === null || gestureStartY.current === null) return;
    
    // Check if touch target is a card (let card handle its own swipe)
    const target = e.target as HTMLElement;
    if (target.closest('[data-card-wrapper]')) {
      return; // Let card handle the gesture
    }
    
    const touch = e.touches[0];
    const deltaX = touch.clientX - gestureStartX.current;
    const deltaY = touch.clientY - gestureStartY.current;
    const absDeltaX = Math.abs(deltaX);
    const absDeltaY = Math.abs(deltaY);
    
    // Determine gesture intent if still undecided
    if (gestureIntentRef.current === "undecided") {
      // Horizontal intent: |deltaX| > |deltaY| and exceeds threshold
      if (absDeltaX > absDeltaY && absDeltaX > HORIZONTAL_THRESHOLD) {
        gestureIntentRef.current = "horizontal";
      }
      // Vertical intent: |deltaY| > |deltaX| and upward swipe exceeds threshold
      else if (absDeltaY > absDeltaX && deltaY < -VERTICAL_THRESHOLD) {
        gestureIntentRef.current = "vertical";
      }
    }
    
    // Handle horizontal swipe in compact mode (background area)
    if (handUIMode === "compact" && gestureIntentRef.current === "horizontal") {
      // Update horizontal offset (use ref to avoid re-renders during drag)
      const baseOffset = horizontalOffsetRef.current;
      const newOffset = baseOffset + deltaX;
      
      // Clamp to reasonable bounds during drag
      const cardSpacing = 35;
      const maxOffset = Math.max(0, (sortedCardsWithIndices.length - 1) * cardSpacing * 0.5);
      const clampedOffset = Math.max(-maxOffset, Math.min(maxOffset, newOffset));
      
      // Apply transform directly to hand container for smooth following
      if (handContainerRef.current) {
        handContainerRef.current.style.transform = `translateX(${clampedOffset}px)`;
      }
    }
    
    // Allow scrolling in expanded mode (no change needed)
  };

  const handleHandTouchEnd = (e: React.TouchEvent) => {
    if (!isMobile || gestureStartX.current === null || gestureStartY.current === null) return;
    
    // Check if touch target is a card (let card handle its own swipe)
    const target = e.target as HTMLElement;
    if (target.closest('[data-card-wrapper]')) {
      // Reset gesture state but don't process hand gesture
      gestureIntentRef.current = "undecided";
      gestureStartX.current = null;
      gestureStartY.current = null;
      handSwipeStartY.current = null;
      return;
    }
    
    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - gestureStartX.current;
    const deltaY = touch.clientY - gestureStartY.current;
    const absDeltaX = Math.abs(deltaX);
    const absDeltaY = Math.abs(deltaY);
    
    // Final gesture intent determination if still undecided
    if (gestureIntentRef.current === "undecided") {
      if (absDeltaX > absDeltaY && absDeltaX > HORIZONTAL_THRESHOLD) {
        gestureIntentRef.current = "horizontal";
      } else if (absDeltaY > absDeltaX && deltaY < -VERTICAL_THRESHOLD) {
        gestureIntentRef.current = "vertical";
      }
    }
    
    // Handle horizontal swipe end - snap to nearest card
    if (handUIMode === "compact" && gestureIntentRef.current === "horizontal") {
      // Calculate snap position based on card width and current offset
      // Cards are spaced approximately 35px apart (from cardWrapper marginLeft: -35px)
      const cardSpacing = 35;
      const currentOffset = horizontalOffsetRef.current + deltaX;
      const snapOffset = Math.round(currentOffset / cardSpacing) * cardSpacing;
      
      // Clamp to reasonable bounds (prevent scrolling too far)
      // Allow scrolling up to half the hand width
      const maxOffset = Math.max(0, (sortedCardsWithIndices.length - 1) * cardSpacing * 0.5);
      const clampedOffset = Math.max(-maxOffset, Math.min(maxOffset, snapOffset));
      
      setHorizontalOffset(clampedOffset);
      horizontalOffsetRef.current = clampedOffset;
      
      // Reset transform (will be handled by state update)
      if (handContainerRef.current) {
        handContainerRef.current.style.transform = "";
      }
      
      // Reset gesture state
      gestureIntentRef.current = "undecided";
      gestureStartX.current = null;
      gestureStartY.current = null;
      handSwipeStartY.current = null;
      return;
    }
    
    // Handle vertical swipe (existing behavior)
    if (gestureIntentRef.current === "vertical" || gestureIntentRef.current === "undecided") {
      const swipeDistance = handSwipeStartY.current! - touch.clientY;
      const SWIPE_THRESHOLD = 50; // Minimum swipe distance
      
      if (handUIMode === "compact" && swipeDistance > SWIPE_THRESHOLD) {
        // Swipe up in compact mode - expand hand
        handleHandExpand();
      } else if ((handUIMode === "expanded" || handUIMode === "focused") && swipeDistance < -SWIPE_THRESHOLD) {
        // Swipe down in expanded/focused mode - collapse
        handleHandCollapse();
      }
    }
    
    // Reset gesture state
    gestureIntentRef.current = "undecided";
    gestureStartX.current = null;
    gestureStartY.current = null;
    handSwipeStartY.current = null;
    
    // Reset transform if it was set
    if (handContainerRef.current) {
      handContainerRef.current.style.transform = "";
    }
  };

  const handleCardClick = (cardIndex: number) => {
    console.log("[handleCardClick] Called", { cardIndex, isMyTurn, roomId, sessionRole, isMobile, handUIMode, focusedCardIndex });
    if (!isMyTurn || !roomId) {
      console.log("[handleCardClick] Blocked: not my turn or no roomId", { isMyTurn, roomId });
      return;
    }
    // Block if passive session
    if (sessionRole === "passive") {
      console.log("[handleCardClick] Blocked: passive session");
      return;
    }

    // Mobile: handle focused mode card play
    if (isMobile && handUIMode === "focused" && focusedCardIndex === cardIndex) {
      const card = myHand[cardIndex];
      const canPlay = canPlayCard(card, cardIndex);
      console.log("[handleCardClick] Mobile focused mode", { card, canPlay });
      if (!canPlay) {
        console.log("[handleCardClick] Card not playable in focused mode");
        handleCardUnfocus();
        return;
      }
      
      if (card.color === CardColor.WILD) {
        setPendingCardIndex(cardIndex);
        setShowColorPicker(true);
        handleCardUnfocus();
      } else {
        const cardElement = draggedCardRefs.current.get(cardIndex);
        const discardPileElement = discardPileRef.current;
        
        if (cardElement && discardPileElement) {
          const cardRect = cardElement.getBoundingClientRect();
          const discardRect = discardPileElement.getBoundingClientRect();
          
          setFlyingCard({
            card,
            from: cardRect,
            to: discardRect,
          });
        }
        triggerHaptic("light");
        playCard(cardIndex);
        handleCardUnfocus();
      }
      return;
    }

    // Mobile: handle expanded mode - focus card
    if (isMobile && handUIMode === "expanded") {
      handleCardFocus(cardIndex);
      return;
    }

    // Desktop or compact mode: normal click behavior
    const card = myHand[cardIndex];
    
    // Check if card is playable before triggering animation
    const canPlay = canPlayCard(card, cardIndex);
    console.log("[handleCardClick] Desktop/compact mode", { card, cardIndex, canPlay, isMyTurn, currentColor: gameState.currentColor, topCard, pendingDraw: gameState.pendingDraw });
    if (!canPlay) {
      console.log("[handleCardClick] Card not playable", { card, currentColor: gameState.currentColor, topCard, pendingDraw: gameState.pendingDraw });
      return;
    }
    
    if (card.color === CardColor.WILD) {
      // For wild cards, don't trigger animation yet - wait for color selection
      setPendingCardIndex(cardIndex);
      setShowColorPicker(true);
    } else {
      // For non-wild cards, capture positions and trigger animation immediately
      const cardElement = draggedCardRefs.current.get(cardIndex);
      const discardPileElement = discardPileRef.current;
      
      if (cardElement && discardPileElement) {
        const cardRect = cardElement.getBoundingClientRect();
        const discardRect = discardPileElement.getBoundingClientRect();
        
        // Trigger flying card animation
        setFlyingCard({
          card,
          from: cardRect,
          to: discardRect,
        });
      }
      triggerHaptic("light"); // Card play success
      playCard(cardIndex);
    }
  };

  const playCard = (cardIndex: number, chosenColor?: Exclude<Card["color"], CardColor.WILD>) => {
    if (!roomId) return;
    // Block if passive session
    if (sessionRole === "passive") {
      console.log("[Game] Cannot play card: passive session");
      return;
    }

    const request: PlayCardRequest = {
      cardIndex,
      chosenColor,
    };
    socket.playCard(roomId, request);
    setShowColorPicker(false);
    setPendingCardIndex(null);
    setCanPlayDrawnCard(false);
  };

  const handleColorSelect = (color: Exclude<Card["color"], CardColor.WILD>) => {
    if (pendingCardIndex !== null) {
      const card = myHand[pendingCardIndex];
      
      // For wild cards, trigger animation AFTER color selection
      if (discardPileRef.current) {
        const cardElement = draggedCardRefs.current.get(pendingCardIndex);
        if (cardElement) {
          const cardRect = cardElement.getBoundingClientRect();
          const discardRect = discardPileRef.current.getBoundingClientRect();
          
          // Trigger flying card animation now that color is selected
          setFlyingCard({
            card,
            from: cardRect,
            to: discardRect,
          });
        }
      }
      
      triggerHaptic("light"); // Card play success (wild card after color selection)
      playCard(pendingCardIndex, color);
    }
  };

  const handleDrawCard = () => {
    if (!isMyTurn || !roomId) return;
    // Block if passive session
    if (sessionRole === "passive") {
      console.log("[Game] Cannot draw card: passive session");
      return;
    }
    socket.drawCard(roomId);
  };

  const handleCallUNO = () => {
    if (!roomId) return;
    // Block if passive session
    if (sessionRole === "passive") {
      console.log("[Game] Cannot call UNO: passive session");
      return;
    }
    socket.callUNO(roomId);
  };

  const handlePlayDrawnCard = () => {
    if (!roomId || myHand.length === 0) return;
    // Block if passive session
    if (sessionRole === "passive") {
      return;
    }
    // The drawn card is the last card in the hand
    const cardIndex = myHand.length - 1;
    const drawnCard = myHand[cardIndex];
    
    if (drawnCard.color === CardColor.WILD) {
      // For wild cards, don't trigger animation yet - wait for color selection
      setPendingCardIndex(cardIndex);
      setShowColorPicker(true);
    } else {
      // For non-wild cards, capture positions and trigger animation immediately
      const cardElement = draggedCardRefs.current.get(cardIndex);
      const discardPileElement = discardPileRef.current;
      
      if (cardElement && discardPileElement) {
        const cardRect = cardElement.getBoundingClientRect();
        const discardRect = discardPileElement.getBoundingClientRect();
        
        // Trigger flying card animation
        setFlyingCard({
          card: drawnCard,
          from: cardRect,
          to: discardRect,
        });
      }
      playCard(cardIndex);
    }
  };

  const handleSkipDrawnCard = () => {
    if (!roomId) return;
    // Block if passive session
    if (sessionRole === "passive") {
      return;
    }
    // Reset the canPlayDrawnCard state and skip the drawn card
    setCanPlayDrawnCard(false);
    socket.skipDrawnCard(roomId);
  };

  // Drag & Drop handlers
  const handleDragStart = (cardIndex: number, event: any) => {
    if (!isMyTurn) return;
    // Block if passive session
    if (sessionRole === "passive") {
      return;
    }
    setDraggedCardIndex(cardIndex);
    setJustDragged(false); // Reset justDragged when drag starts
    
    // On mobile, track swiping state for visual feedback
    if (isMobile) {
      setSwipingCardIndex(cardIndex);
    }
    
    // Set fixed position for the card wrapper when dragging starts (desktop only)
    if (!isMobile) {
      const wrapper = cardWrapperRefs.current.get(cardIndex);
      if (wrapper) {
        const rect = wrapper.getBoundingClientRect();
        wrapper.style.position = "fixed";
        wrapper.style.left = `${rect.left}px`;
        wrapper.style.top = `${rect.top}px`;
        wrapper.style.width = `${rect.width}px`;
      }
    }
  };

  const handleDrag = (event: any, info?: PanInfo) => {
    // Mobile: highlight discard pile when swiping upward
    if (isMobile && draggedCardIndex !== null && info) {
      if (info.offset.y < -40) {
        setIsOverDiscardPile(true);
      } else {
        setIsOverDiscardPile(false);
      }
      return;
    }
    
    // Desktop: check if card overlaps with discard pile
    if (!discardPileRef.current || draggedCardIndex === null) {
      setIsOverDiscardPile(false);
      return;
    }

    // Get the dragged card element to check its position
    const draggedCardElement = draggedCardRefs.current.get(draggedCardIndex);
    if (!draggedCardElement) {
      setIsOverDiscardPile(false);
      return;
    }

    const discardRect = discardPileRef.current.getBoundingClientRect();
    const cardRect = draggedCardElement.getBoundingClientRect();

    // Check if card center overlaps with discard pile
    const cardCenterX = cardRect.left + cardRect.width / 2;
    const cardCenterY = cardRect.top + cardRect.height / 2;

    const isOver =
      cardCenterX >= discardRect.left &&
      cardCenterX <= discardRect.right &&
      cardCenterY >= discardRect.top &&
      cardCenterY <= discardRect.bottom;

    setIsOverDiscardPile(isOver);
  };

  const handleDragEnd = (event: any, info: PanInfo, cardIndex: number) => {
    const wasDragging = draggedCardIndex === cardIndex;
    
    // Mobile swipe detection
    if (isMobile && wasDragging) {
      const SWIPE_DISTANCE = -80; // Negative because upward swipe has negative y
      const SWIPE_VELOCITY = -500; // Negative because upward swipe has negative y
      
      const swipeUpward = info.offset.y < SWIPE_DISTANCE || info.velocity.y < SWIPE_VELOCITY;
      
      if (swipeUpward) {
        // Check if it's player's turn and card is playable
        if (!isMyTurn || !roomId) {
          setSwipingCardIndex(null);
          setShouldSnapBack(cardIndex);
          setTimeout(() => setShouldSnapBack(null), 500);
          return;
        }
        
        const card = myHand[cardIndex];
        if (!canPlayCard(card, cardIndex)) {
          setSwipingCardIndex(null);
          setShouldSnapBack(cardIndex);
          setTimeout(() => setShouldSnapBack(null), 500);
          return;
        }
        
        // Capture positions for flying card animation
        const draggedCardElement = draggedCardRefs.current.get(cardIndex);
        const discardPileElement = discardPileRef.current;
        
        if (draggedCardElement && discardPileElement) {
          const cardRect = draggedCardElement.getBoundingClientRect();
          const discardRect = discardPileElement.getBoundingClientRect();
          
          setSwipingCardIndex(null);
          setJustDragged(true);
          setTimeout(() => setJustDragged(false), 100);
          
          if (card.color === CardColor.WILD) {
            setPendingCardIndex(cardIndex);
            setShowColorPicker(true);
          } else {
            setFlyingCard({
              card,
              from: cardRect,
              to: discardRect,
            });
            triggerHaptic("light"); // Card play success
            playCard(cardIndex);
          }
        }
        
        // Reset drag state
        setDraggedCardIndex(null);
        setIsOverDiscardPile(false);
        return;
      } else {
        // Swipe down or insufficient swipe - snap back
        setSwipingCardIndex(null);
        setShouldSnapBack(cardIndex);
        setTimeout(() => setShouldSnapBack(null), 500);
        setDraggedCardIndex(null);
        setIsOverDiscardPile(false);
        return;
      }
    }
    
    // Desktop drag & drop logic (existing)
    // Reset swiping state (shouldn't be set on desktop, but reset for safety)
    setSwipingCardIndex(null);
    
    // Capture positions BEFORE resetting anything (for flying card animation)
    const draggedCardElement = draggedCardRefs.current.get(cardIndex);
    const discardPileElement = discardPileRef.current;
    
    // Capture positions immediately while card is still in dragged position
    let cardRect: DOMRect | null = null;
    let discardRect: DOMRect | null = null;
    
    if (draggedCardElement) {
      cardRect = draggedCardElement.getBoundingClientRect();
    }
    if (discardPileElement) {
      discardRect = discardPileElement.getBoundingClientRect();
    }
    
    // Reset fixed position (desktop only)
    const wrapper = cardWrapperRefs.current.get(cardIndex);
    if (wrapper && wrapper.style.position === "fixed") {
      wrapper.style.position = "";
      wrapper.style.left = "";
      wrapper.style.top = "";
      wrapper.style.width = "";
    }
    
    // Reset drag state immediately
    setDraggedCardIndex(null);
    setIsOverDiscardPile(false);

    if (!isMyTurn || !roomId || !wasDragging) {
      return;
    }

    // If no discard pile ref, trigger snap back
    if (!discardPileElement || !discardRect) {
      setShouldSnapBack(cardIndex);
      setTimeout(() => setShouldSnapBack(null), 500);
      return;
    }

    // If no dragged card element, trigger snap back
    if (!draggedCardElement || !cardRect) {
      setShouldSnapBack(cardIndex);
      setTimeout(() => setShouldSnapBack(null), 500);
      return;
    }

    // Check if card center overlaps with discard pile at drag end
    const cardCenterX = cardRect.left + cardRect.width / 2;
    const cardCenterY = cardRect.top + cardRect.height / 2;

    const isOverDiscardPileFinal =
      cardCenterX >= discardRect.left &&
      cardCenterX <= discardRect.right &&
      cardCenterY >= discardRect.top &&
      cardCenterY <= discardRect.bottom;

    // Only play card if dropped on discard pile AND card is playable
    if (isOverDiscardPileFinal) {
      const card = myHand[cardIndex];
      // Check if card is playable (same validation as click)
      if (canPlayCard(card, cardIndex)) {
        setJustDragged(true);
        // Prevent click event from firing
        setTimeout(() => setJustDragged(false), 100);
        
        if (card.color === CardColor.WILD) {
          // For wild cards, don't trigger animation yet - wait for color selection
          setPendingCardIndex(cardIndex);
          setShowColorPicker(true);
        } else {
          // For non-wild cards, trigger animation immediately
          setFlyingCard({
            card,
            from: cardRect,
            to: discardRect,
          });
          triggerHaptic("light"); // Card play success
          playCard(cardIndex);
        }
        return; // Card will be removed from hand, no need to snap back
      }
    }
    
    // If dropped outside discard pile OR card is not playable, trigger snap back
    setShouldSnapBack(cardIndex);
    setTimeout(() => setShouldSnapBack(null), 500);
  };

  const currentColorHex = getColorHex(gameState.currentColor);

  // Handle play again - navigate back to room
  const handlePlayAgain = () => {
    if (roomId) {
      setGameOverState(null);
      navigate(`/room/${roomId}`);
    }
  };

  // Handle back to lobby
  const handleBackToLobby = () => {
    setGameOverState(null);
    const playerName = sessionService.getPlayerName();
    navigate("/rooms", {
      state: { playerName: playerName || "" },
    });
  };

  return (
    <div style={{
      width: "100vw",
      height: "100vh",
      display: "flex",
      justifyContent: "center",
      alignItems: "stretch",
      background: "#111", // Dark neutral background
    }}>
      {/* End Game Screen - Full screen overlay */}
      {gameOverState && gameState && (
        <EndGameScreen
          gameState={gameState}
          winnerId={gameOverState.winnerId}
          winnerName={gameOverState.winnerName}
          currentUserId={userId}
          onPlayAgain={handlePlayAgain}
          onBackToLobby={handleBackToLobby}
        />
      )}

      {/* Game Canvas - Full height, max 350px width on desktop, centered */}
      <div style={{
        width: "100%",
        maxWidth: "350px",
        height: "100vh",
        margin: "0 auto",
        position: "relative",
        overflow: "hidden",
        background: "#4a90e2", // Bright blue board background
      }}>
      
      {error && (
        <div style={{
          position: "absolute",
          top: "60px",
          left: "50%",
          transform: "translateX(-50%)",
          padding: "8px 16px",
          background: "#e74c3c",
          color: "white",
          borderRadius: "8px",
          fontSize: "12px",
          zIndex: 1000,
          boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
        }}>
          {error}
        </div>
      )}

      {/* TOP BAR - Fixed height ~50px */}
      <div style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: "50px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 12px",
        zIndex: 100,
      }}>
        {/* Left: Back Arrow Button */}
        <button 
          onClick={() => {
            const playerName = sessionService.getPlayerName();
            navigate("/rooms", {
              state: { playerName: playerName || "" },
            });
          }}
          style={{
            width: "32px",
            height: "32px",
            borderRadius: "50%",
            background: "rgba(255,255,255,0.2)",
            border: "none",
            color: "white",
            fontSize: "20px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
          }}
          title="Back"
        >
          ←
        </button>

        {/* Center: Room Title */}
        <div style={{
          fontSize: "14px",
          fontWeight: "700",
          color: "white",
          textAlign: "center",
          flex: 1,
          textShadow: "0 1px 2px rgba(0,0,0,0.3)",
        }}>
          {roomId ? `Room ${roomId.slice(0, 6)}` : "UNO Game"}
        </div>
      </div>

      {/* CIRCULAR OPPONENTS LAYOUT - Arranged around center */}
      {gameState && (
        <div style={{
          position: "absolute",
          top: "50px",
          left: 0,
          right: 0,
          bottom: "180px", // Space for hand
          pointerEvents: "none",
          zIndex: 10,
        }}>
          {gameState.players
            .filter((p) => p.id !== userId)
            .map((opponent, index) => {
              const isActive = opponent.id === gameState.currentTurnPlayerId;
              const totalOpponents = gameState.players.filter((p) => p.id !== userId).length;
              
              // Circular positions around center
              const angle = (index / totalOpponents) * 2 * Math.PI - Math.PI / 2; // Start from top
              const radius = 120; // Distance from center
              const centerX = 175; // 350 / 2
              const centerY = 200; // Approximate center of play area
              
              const x = centerX + Math.cos(angle) * radius;
              const y = centerY + Math.sin(angle) * radius;
              
              return (
                <motion.div
                  key={opponent.id}
                  style={{
                    position: "absolute",
                    left: `${x}px`,
                    top: `${y}px`,
                    transform: "translate(-50%, -50%)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "4px",
                    pointerEvents: "auto",
                  }}
                  animate={{
                    scale: isActive ? 1.1 : 1,
                  }}
                  transition={{ duration: 0.2, ease: "easeInOut" }}
                >
                  {/* Avatar */}
                  <div style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "8px", // Rounded square
                    background: isActive ? "#ffd700" : "#8e8e93",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "18px",
                    fontWeight: "700",
                    color: "white",
                    boxShadow: isActive 
                      ? "0 0 0 2px #ffd700, 0 4px 8px rgba(0,0,0,0.3)"
                      : "0 2px 4px rgba(0,0,0,0.2)",
                  }}>
                    {opponent.name.charAt(0).toUpperCase()}
                  </div>
                  
                  {/* Card Count Badge */}
                  <div style={{
                    padding: "2px 6px",
                    borderRadius: "8px",
                    background: "rgba(0,0,0,0.5)",
                    color: "white",
                    fontSize: "10px",
                    fontWeight: "700",
                    minWidth: "20px",
                    textAlign: "center",
                  }}>
                    {opponent.cardsCount}
                  </div>
                </motion.div>
              );
            })}
        </div>
      )}

      {/* CENTER TABLE AREA - Discard Pile & Draw Deck */}
      <div style={{
        position: "absolute",
        top: "50px",
        left: 0,
        right: 0,
        bottom: "180px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 5,
      }}>
        {/* Draw Deck */}
        {isMyTurn && !canPlayDrawnCard && (
          <div
            style={{
              position: "absolute",
              left: "50px",
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 1,
            }}
          >
            <DrawDeck
              deckCount={(gameState.deck && Array.isArray(gameState.deck)) ? gameState.deck.length : 0}
              onClick={handleDrawCard}
              isMyTurn={isMyTurn}
              isMobile={true}
              disabled={!isMyTurn || sessionRole === "passive"}
              pulseIntensity="normal"
            />
          </div>
        )}

        {/* Discard Pile - Centered */}
        <div
          ref={discardPileRef}
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 2,
          }}
        >
          <DiscardPile
            discardPile={gameState.discardPile}
            currentColor={gameState.currentColor}
            currentColorHex={currentColorHex}
            isOverDiscardPile={isOverDiscardPile}
            draggedCardIndex={draggedCardIndex}
            swipingCardIndex={swipingCardIndex}
            drawStackUI={drawStackUI}
            discardPileRef={discardPileRef}
            flyingCard={flyingCard}
            isMobile={true}
            isMyTurn={isMyTurn}
            penaltyAnimationState={penaltyAnimationState}
            prefersReducedMotion={prefersReducedMotion}
          />
        </div>

        {/* Direction Arrow Indicator */}
        {showDirectionIndicator && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.2 }}
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              fontSize: "32px",
              color: "rgba(255,255,255,0.6)",
              zIndex: 1,
              pointerEvents: "none",
            }}
          >
            {gameState.direction === "clockwise" ? "↻" : "↺"}
          </motion.div>
        )}
      </div>

      {/* Drawn Card Display - Centered when available */}
      {!isSpectator && isMyTurn && canPlayDrawnCard && myHand.length > 0 && (
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.8, opacity: 0 }}
          transition={{ type: "spring", stiffness: 300, damping: 25 }}
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 2000,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "16px",
          }}
        >
          {/* Drawn Card */}
          <motion.div
            animate={{
              scale: [1, 1.05, 1],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          >
            <CardSVG card={myHand[myHand.length - 1]} scale={0.15} />
          </motion.div>
          
          {/* Action Buttons */}
          <div style={{
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            alignItems: "center",
          }}>
            <motion.button
              onClick={handlePlayDrawnCard}
              disabled={sessionRole === "passive"}
              whileTap={{ scale: 0.95 }}
              style={{
                padding: "10px 20px",
                fontSize: "14px",
                fontWeight: "700",
                color: "white",
                background: "#2ecc71",
                border: "none",
                borderRadius: "12px",
                cursor: sessionRole === "passive" ? "not-allowed" : "pointer",
                opacity: sessionRole === "passive" ? 0.5 : 1,
                boxShadow: "0 4px 12px rgba(46, 204, 113, 0.4)",
              }}
            >
              Play Card
            </motion.button>
            
            <motion.button
              onClick={handleSkipDrawnCard}
              disabled={sessionRole === "passive"}
              whileTap={{ scale: 0.95 }}
              style={{
                padding: "10px 20px",
                fontSize: "14px",
                fontWeight: "600",
                color: "white",
                background: "rgba(100, 100, 100, 0.8)",
                border: "none",
                borderRadius: "12px",
                cursor: sessionRole === "passive" ? "not-allowed" : "pointer",
                opacity: sessionRole === "passive" ? 0.5 : 1,
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
              }}
            >
              Cancel
            </motion.button>
          </div>
        </motion.div>
      )}
        
      {/* BOTTOM PLAYER HAND - Fanned horizontally */}
      <div style={{
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        height: "180px",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        paddingBottom: "20px",
        overflow: "visible",
        zIndex: 20,
      }}>
        {!isSpectator && (
          <div style={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "center",
            position: "relative",
            height: "100%",
            width: "100%",
          }}>
            {sortedCardsWithIndices.map(({ card, originalIndex }, displayIndex) => {
              const isPassive = sessionRole === "passive";
              const canPlay = canPlayCard(card, originalIndex);
              const isDisabled = !isMyTurn || !canPlay || isPassive;
              const isDragging = draggedCardIndex === originalIndex;
              const isSwiping = swipingCardIndex === originalIndex;
              const isPlayable = !isDisabled && canPlay;
              
              // Debug logging for first card only to avoid spam
              if (originalIndex === 0) {
                console.log("[Card render] First card state", { 
                  card, 
                  originalIndex, 
                  isMyTurn, 
                  canPlay, 
                  isPassive, 
                  isDisabled, 
                  currentColor: gameState.currentColor, 
                  topCard, 
                  pendingDraw: gameState.pendingDraw 
                });
              }
              
              // Calculate fanned position - horizontal fan with slight overlap
              const totalCards = sortedCardsWithIndices.length;
              const cardWidth = 45; // Approximate card width at scale 0.15 (300 * 0.15 = 45)
              const overlap = 20; // Increased overlap - cards closer together
              const totalWidth = Math.min(totalCards * (cardWidth - overlap) + overlap, 330);
              const startX = (350 - totalWidth) / 2;
              const cardX = startX + displayIndex * (cardWidth - overlap);
              
              return (
                <motion.div
                  key={`${card.color}-${card.value}-${originalIndex}`}
                  ref={(el: HTMLDivElement | null) => {
                    if (el) {
                      cardWrapperRefs.current.set(originalIndex, el);
                    } else {
                      cardWrapperRefs.current.delete(originalIndex);
                    }
                  }}
                  animate={{
                    x: cardX - 175, // Offset from center (175 = 350/2)
                    y: 0,
                    rotate: 0,
                    scale: isSwiping ? 1.15 : 1,
                    opacity: isDisabled ? 0.4 : 1,
                  }}
                  transition={{ duration: 0.2, ease: "easeInOut" }}
                  style={{
                    position: "absolute",
                    left: "50%",
                    bottom: 0,
                    zIndex: isDragging ? 2000 : (isSwiping ? 1500 : displayIndex),
                    cursor: isDisabled ? "not-allowed" : "grab",
                    boxShadow: isPlayable && isMyTurn && !isDragging
                      ? "0 4px 12px rgba(0,0,0,0.3)"
                      : "0 2px 6px rgba(0,0,0,0.2)",
                  }}
                  onTouchStart={(e: React.TouchEvent) => handleCardTouchStart(e, originalIndex)}
                  onTouchMove={(e: React.TouchEvent) => handleCardTouchMove(e, originalIndex)}
                  onTouchEnd={(e: React.TouchEvent) => handleCardTouchEnd(e, originalIndex)}
                >
                  <motion.div
                    ref={(el: HTMLDivElement | null) => {
                      if (el) {
                        draggedCardRefs.current.set(originalIndex, el);
                      } else {
                        draggedCardRefs.current.delete(originalIndex);
                      }
                    }}
                    drag={isMyTurn && !isPassive && sessionRole === "active" ? "y" : false}
                    dragElastic={0.2}
                    dragMomentum={false}
                    dragConstraints={{ top: -120, bottom: 0 }}
                    dragListener={true} // Enable drag listener
                    onDragStart={(event: any) => handleDragStart(originalIndex, event)}
                    onDrag={handleDrag}
                    onDragEnd={(event: any, info: PanInfo) => handleDragEnd(event, info, originalIndex)}
                    whileDrag={{
                      scale: 1.1,
                      zIndex: 2000,
                      rotate: -3,
                      boxShadow: "0 8px 16px rgba(0,0,0,0.3)",
                    }}
                    whileTap={{ scale: 0.95 }}
                    style={{
                      cursor: isDragging ? "grabbing" : isDisabled ? "not-allowed" : "grab",
                      touchAction: "pan-y", // Allow vertical panning for drag
                    }}
                    onTap={(e: any) => {
                      // onTap fires when user taps/clicks without dragging
                      // framer-motion automatically prevents onTap if there was drag movement
                      if (isDisabled) {
                        console.log("[Card onTap] Blocked: disabled", { originalIndex, isDisabled, isMyTurn, canPlay });
                        return;
                      }
                      console.log("[Card onTap] Tapped - playing card", { originalIndex, isDisabled, isMobile, isMyTurn });
                      handleCardClick(originalIndex);
                    }}
                    onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                      // Fallback click handler for cases where onTap doesn't fire
                      // This can happen in some browsers or edge cases
                      e.stopPropagation();
                      // Don't handle if we just dragged (prevents double-firing)
                      if (justDragged) {
                        console.log("[Card onClick] Blocked: justDragged", { originalIndex });
                        return;
                      }
                      if (isDisabled) {
                        console.log("[Card onClick] Blocked: disabled", { originalIndex });
                        return;
                      }
                      // Only use as fallback on desktop (mobile should use onTap or touch handlers)
                      if (!isMobile) {
                        console.log("[Card onClick] Fallback click handler", { originalIndex });
                        handleCardClick(originalIndex);
                      }
                    }}
                  >
                    <CardSVG card={card} scale={0.15} />
                  </motion.div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Color Picker Modal */}
      {showColorPicker && (
        <ColorPicker
          onSelect={handleColorSelect}
          onCancel={() => {
            setShowColorPicker(false);
            setPendingCardIndex(null);
          }}
        />
      )}

      {/* Help Modal */}
      {showHelpModal && (
        <HelpModal onClose={() => setShowHelpModal(false)} />
      )}

      {/* Log Modal */}
      {showLogModal && (
        <LogModal 
          logs={gameLogs} 
          onClose={() => setShowLogModal(false)} 
        />
      )}

      {/* Flying Card Animation */}
      <AnimatePresence>
        {flyingCard && (
          <FlyingCard
            key={`flying-${flyingCard.card.color}-${flyingCard.card.value}`}
            flyingCard={flyingCard}
            onAnimationComplete={() => {
              setFlyingCard(null);
            }}
          />
        )}
      </AnimatePresence>

      {/* Draw Storm Animation */}
      <AnimatePresence>
        {drawStormCards.map((stormCard, index) => (
          <FlyingCard
            key={`storm-${index}`}
            flyingCard={stormCard}
            onAnimationComplete={() => {
              setDrawStormCards((prev) => prev.filter((_, i) => i !== index));
            }}
          />
        ))}
      </AnimatePresence>

      {/* UNO Warning Animation Keyframes - Shake + Pop (not flashing) */}
      <style>{`
        @keyframes unoWarningPulse {
          0%, 100% { 
            box-shadow: inset 0 0 100px rgba(231, 76, 60, 0.15);
          }
          50% { 
            box-shadow: inset 0 0 120px rgba(231, 76, 60, 0.25);
          }
        }
        @keyframes unoWarningShake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
          20%, 40%, 60%, 80% { transform: translateX(4px); }
        }
        @keyframes unoWarningPop {
          0% { transform: scale(1); }
          50% { transform: scale(1.15); }
          100% { transform: scale(1); }
        }
        @keyframes drawDeckPulse {
          0%, 100% { 
            transform: scale(1);
          }
          50% { 
            transform: scale(1.05);
          }
        }
      `}</style>

      {/* UNO Button - Centered, big, red */}
      <AnimatePresence>
        {needsUNO && (
          <motion.button
            ref={unoButtonRef}
            onClick={handleCallUNO}
            disabled={sessionRole === "passive"}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{
              scale: unoUXState.urgent && sessionRole === "active" 
                ? [1, 1.1, 1]
                : 1,
              opacity: 1,
            }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{
              scale: {
                duration: 0.6,
                repeat: (unoUXState.urgent && sessionRole === "active") ? Infinity : 0,
                ease: "easeInOut",
              },
              opacity: { duration: 0.3 },
            }}
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "80px",
              height: "80px",
              borderRadius: "50%",
              background: unoUXState.urgent ? "#e74c3c" : "#c0392b",
              border: "none",
              color: "white",
              fontSize: "18px",
              fontWeight: "900",
              cursor: sessionRole === "passive" ? "not-allowed" : "pointer",
              opacity: sessionRole === "passive" ? 0.5 : 1,
              boxShadow: "0 4px 16px rgba(231, 76, 60, 0.6), 0 2px 8px rgba(0,0,0,0.3)",
              zIndex: 2000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textTransform: "uppercase",
              letterSpacing: "1px",
            }}
            title={unoUXState.urgent ? "Tap to call UNO!" : "Call UNO!"}
          >
            UNO
          </motion.button>
        )}
      </AnimatePresence>

      {/* UNO Reminder (when player has 2 cards) - Visual only, no text */}
      <AnimatePresence>
        {unoUXState.needsReminder && isMyTurn && !needsUNO && (
          <motion.div
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0 }}
            transition={{ duration: 0.3 }}
            style={{
              position: "fixed",
              bottom: isMobile ? "calc(38vh + 20px)" : "calc(40vh + 24px)",
              right: isMobile ? "20px" : "32px",
              width: isMobile ? "12px" : "16px",
              height: isMobile ? "12px" : "16px",
              borderRadius: "50%",
              background: "rgba(255, 193, 7, 0.9)",
              boxShadow: "0 0 20px rgba(255, 193, 7, 0.8)",
              zIndex: 1999,
            }}
          />
        )}
      </AnimatePresence>

      {/* UNO Call Animation - Self */}
      <UnoCallAnimation
        active={unoUXState.successActive}
        isSelf={true}
      />

      {/* UNO Call Animation - Other Players */}
      {otherPlayerUnoCall && (
        <UnoCallAnimation
          active={otherPlayerUnoCall.active}
          playerName={otherPlayerUnoCall.playerName}
          isSelf={false}
        />
      )}

      {/* Penalty Animation (Draw +2, Draw +4, Missed UNO) */}
      {penaltyState.type && (
        <PenaltyAnimation
          active={penaltyState.active}
          type={penaltyState.type}
          cardsCount={penaltyState.cardsCount}
          prefersReducedMotion={prefersReducedMotion}
        />
      )}

      {/* Modals and Animations */}
      {showColorPicker && (
        <ColorPicker
          onSelect={handleColorSelect}
          onCancel={() => {
            setShowColorPicker(false);
            setPendingCardIndex(null);
          }}
        />
      )}

      {showHelpModal && (
        <HelpModal onClose={() => setShowHelpModal(false)} />
      )}

      {showLogModal && (
        <LogModal 
          logs={gameLogs} 
          onClose={() => setShowLogModal(false)} 
        />
      )}

      {/* Flying Card Animation */}
      <AnimatePresence>
        {flyingCard && (
          <FlyingCard
            key={`flying-${flyingCard.card.color}-${flyingCard.card.value}`}
            flyingCard={flyingCard}
            onAnimationComplete={() => {
              setFlyingCard(null);
            }}
          />
        )}
      </AnimatePresence>

      {/* Draw Storm Animation */}
      <AnimatePresence>
        {drawStormCards.map((stormCard, index) => (
          <FlyingCard
            key={`storm-${index}`}
            flyingCard={stormCard}
            onAnimationComplete={() => {
              setDrawStormCards((prev) => prev.filter((_, i) => i !== index));
            }}
          />
        ))}
      </AnimatePresence>

      {/* UNO Call Animation - Self */}
      <UnoCallAnimation
        active={unoUXState.successActive}
        isSelf={true}
      />

      {/* UNO Call Animation - Other Players */}
      {otherPlayerUnoCall !== null && (
        <UnoCallAnimation
          active={otherPlayerUnoCall.active}
          playerName={otherPlayerUnoCall.playerName}
          isSelf={false}
        />
      )}

      {/* Penalty Animation */}
      {penaltyState.type !== null && (
        <PenaltyAnimation
          active={penaltyState.active}
          type={penaltyState.type}
          cardsCount={penaltyState.cardsCount}
          prefersReducedMotion={prefersReducedMotion}
        />
      )}
      </div>
    </div>
  );
}

function getColorHex(color: string): string {
  // Use CSS variables for card colors
  const root = document.documentElement;
  const colorMap: Record<string, string> = {
    red: "--card-red",
    blue: "--card-blue",
    green: "--card-green",
    yellow: "--card-yellow",
  };
  const cssVar = colorMap[color];
  if (cssVar) {
    return getComputedStyle(root).getPropertyValue(cssVar).trim() || "#95a5a6";
  }
  return "#95a5a6";
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    height: "97vh",
    maxHeight: "97vh", // Prevent overflow
    // Background is set dynamically in component based on theme
    position: "relative",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    // NO safe-area padding on container - cards need full screen space
    // Constrain game board to max-width on desktop, center horizontally
    alignItems: "center",
  },
  gameBoard: {
    width: "100%",
    maxWidth: "900px", // Max width constraint for desktop
    height: "100%",
    maxHeight: "100%", // Prevent overflow
    display: "flex",
    flexDirection: "column",
    position: "relative",
    overflow: "hidden", // Prevent content overflow
    paddingTop: "60px", // Space for top bar
    paddingBottom: "20px", // Reduced padding since bottom bar removed
  },
  backgroundOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    // Enhanced vignette effect for game feel
    background: `
      radial-gradient(ellipse at center top, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.5) 100%),
      radial-gradient(ellipse at center bottom, rgba(0,0,0,0.3) 0%, transparent 100%)
    `,
    pointerEvents: "none",
    zIndex: 0,
  },
  loading: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "white",
    fontSize: "24px",
    fontWeight: "600",
  },
  error: {
    position: "fixed",
    top: "20px",
    left: "50%",
    transform: "translateX(-50%)",
    padding: "12px 24px",
    background: "var(--btn-danger)",
    color: "white",
    borderRadius: "12px",
    zIndex: 1000,
    fontSize: "14px",
    boxShadow: "0 4px 12px color-mix(in srgb, var(--btn-danger) 50%, transparent)",
    // backdropFilter removed for Casual Party (handled via CSS variable in global.css)
  },
  helpButton: {
    position: "fixed",
    top: "20px",
    right: "20px",
    width: "48px",
    height: "48px",
    borderRadius: "50%",
    background: "var(--bg-card)",
    // backdropFilter removed for Casual Party (solid backgrounds, no blur)
    border: "1px solid var(--border-medium)",
    color: "var(--text)",
    fontSize: "24px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 100,
    // Snappy transitions: 200ms
    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    // Ensure minimum tap target on mobile
    minWidth: "44px",
    minHeight: "44px",
  },
  helpButtonHovered: {
    background: "var(--bg-card-hover)",
    transform: "scale(1.1)",
    boxShadow: "0 6px 16px rgba(0,0,0,0.2)",
  },
  logButton: {
    position: "fixed",
    top: "20px",
    right: "80px",
    width: "48px",
    height: "48px",
    borderRadius: "50%",
    background: "var(--bg-card)",
    // backdropFilter removed for Casual Party (solid backgrounds, no blur)
    border: "1px solid var(--border-medium)",
    color: "var(--text)",
    fontSize: "24px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 100,
    // Snappy transitions: 200ms
    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    // Ensure minimum tap target on mobile
    minWidth: "44px",
    minHeight: "44px",
  },
  logButtonHovered: {
    background: "var(--bg-card-hover)",
    transform: "scale(1.1)",
    boxShadow: "0 6px 16px rgba(0,0,0,0.2)",
  },
  backButton: {
    position: "fixed",
    top: "20px",
    left: "20px",
    width: "48px",
    height: "48px",
    borderRadius: "50%",
    background: "var(--bg-card)",
    // backdropFilter removed for Casual Party (solid backgrounds, no blur)
    border: "1px solid var(--border-medium)",
    color: "var(--text)",
    fontSize: "24px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 100,
    // Snappy transitions: 200ms
    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    // Ensure minimum tap target on mobile
    minWidth: "44px",
    minHeight: "44px",
  },
  backButtonHovered: {
    background: "var(--bg-card-hover)",
    transform: "scale(1.1)",
    boxShadow: "0 6px 16px rgba(0,0,0,0.2)",
  },
  // Top Bar Styles - Match Design
  topBar: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    height: "60px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 16px",
    paddingTop: "max(8px, env(safe-area-inset-top))",
    background: "rgba(0, 0, 0, 0.3)",
    backdropFilter: "blur(10px)",
    zIndex: 1000,
    borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
  },
  topBarBackButton: {
    width: "44px",
    height: "44px",
    borderRadius: "50%", // Circular
    background: "#FF6B35", // Orange circle
    border: "none",
    color: "#FFD700", // Yellow arrow
    fontSize: "24px",
    fontWeight: "700",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 4px 12px rgba(255, 107, 53, 0.3)",
    transition: "all 0.2s ease",
  },
  topBarBackButtonHovered: {
    transform: "scale(1.05)",
    boxShadow: "0 6px 16px rgba(255, 107, 53, 0.5)",
  },
  // League Badge - REMOVED per user request
  topBarTimer: {
    display: "flex",
    alignItems: "center",
    padding: "4px 8px",
    borderRadius: "12px",
    background: "rgba(255, 255, 255, 0.1)",
    // Small text badge - minimal
  },
  timerText: {
    fontSize: "11px",
    fontWeight: "500",
    color: "rgba(255, 255, 255, 0.8)",
  },
  // League Badge - All Hands League
  leagueBadge: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "6px 12px",
    borderRadius: "8px",
    background: "#FFD700", // Yellow shield background
    border: "2px solid rgba(0, 0, 0, 0.2)",
    boxShadow: "0 2px 8px rgba(255, 215, 0, 0.3)",
  },
  leagueIcon: {
    fontSize: "20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  leagueText: {
    fontSize: "12px",
    fontWeight: "700",
    color: "#000",
    whiteSpace: "nowrap",
  },
  // Share Game Button - Left Side
  shareGameButton: {
    position: "fixed",
    left: "16px",
    top: "80px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "10px 16px",
    borderRadius: "24px",
    background: "#FF6B35", // Orange
    border: "none",
    color: "#fff",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    boxShadow: "0 4px 12px rgba(255, 107, 53, 0.3)",
    transition: "all 0.2s ease",
    zIndex: 1000,
  },
  shareGameButtonHovered: {
    transform: "scale(1.05)",
    boxShadow: "0 6px 16px rgba(255, 107, 53, 0.5)",
  },
  // Bottom Bar Styles - REMOVED (no longer used)
  emojiButton: {
    width: "44px",
    height: "44px",
    borderRadius: "12px",
    background: "#FFD700",
    border: "none",
    fontSize: "24px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 4px 12px rgba(255, 215, 0, 0.3)",
    transition: "all 0.2s ease",
  },
  // Currency Display - REMOVED per user request
  plusButton: {
    width: "44px",
    height: "44px",
    borderRadius: "12px",
    background: "#2ECC71",
    border: "none",
    color: "white",
    fontSize: "28px",
    fontWeight: "700",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 4px 12px rgba(46, 204, 113, 0.3)",
    transition: "all 0.2s ease",
  },
  // Auto Play Container - REMOVED per user request
  toggleButton: {
    width: "70px",
    height: "28px",
    borderRadius: "14px",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    padding: "2px",
    transition: "all 0.3s ease",
    position: "relative",
    boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
  },
  toggleLabel: {
    fontSize: "9px",
    fontWeight: "700",
    color: "white",
    padding: "0 4px",
    whiteSpace: "nowrap",
  },
  // TOP ZONE - Opponents & Turn Indicator (15-20% height)
  topZone: {
    position: "relative",
    zIndex: 1,
    flexShrink: 0,
    flexGrow: 0, // Don't grow beyond content
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    // Subtle background separation - theme-aware
    background: "linear-gradient(to bottom, color-mix(in srgb, var(--text) 10%, transparent) 0%, transparent 100%)",
    overflow: "visible",
    maxHeight: "25vh", // Limit max height to prevent overflow
  },
  infoBar: {
    display: "flex",
    gap: "8px", // Reduced gap for compact layout
    justifyContent: "center",
    marginBottom: "8px", // Reduced margin
    flexWrap: "wrap",
    paddingLeft: "0",
    paddingRight: "0",
    maxWidth: "100%",
    boxSizing: "border-box",
    flexShrink: 0,
  },
  infoItem: {
    color: "rgba(255,255,255,0.95)", // Slightly muted for game feel
    fontSize: "14px", // Slightly smaller for compact layout
    display: "flex",
    alignItems: "center",
    gap: "6px", // Reduced gap
    flexWrap: "wrap",
  },
  infoLabel: {
    opacity: 0.7, // More muted label
    fontWeight: "500",
    fontSize: "12px", // Smaller label text
  },
  colorBadge: {
    padding: "6px 16px",
    borderRadius: "20px",
    color: "white",
    fontWeight: "700",
    fontSize: "14px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    // Enhanced depth with inner highlight
    boxShadow: "0 4px 12px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.2)",
    border: "1px solid rgba(255,255,255,0.2)",
  },
  warning: {
    color: "var(--uno-active)",
    fontWeight: "700",
    fontSize: "18px",
    textShadow: "0 0 10px rgba(255, 235, 59, 0.5)",
  },
  opponents: {
    display: "flex",
    justifyContent: "center",
    gap: "8px", // Reduced gap for compact layout
    flexWrap: "wrap",
    flexShrink: 0,
  },
  opponent: {
    background: "rgba(255,255,255,0.08)", // Slightly more transparent for game feel
    // backdropFilter removed for Casual Party (solid backgrounds, no blur)
    padding: "8px 14px", // Reduced padding for compact layout
    borderRadius: "12px", // Slightly less rounded for compact feel
    textAlign: "center",
    minWidth: "100px", // Reduced min width
    // Snappy transitions: 200ms
    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
    border: "1px solid rgba(255,255,255,0.1)",
    // Subtle depth
    boxShadow: "0 3px 8px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)",
  },
  opponentActive: {
    background: "rgba(255, 235, 59, 0.25)",
    transform: "scale(1.05)",
    boxShadow: "0 6px 24px rgba(255, 235, 59, 0.5), 0 2px 8px rgba(255, 235, 59, 0.3), inset 0 1px 0 rgba(255,255,255,0.1)",
    border: "1px solid rgba(255, 235, 59, 0.4)",
  },
  opponentName: {
    fontWeight: "600",
    fontSize: "13px", // Smaller for compact layout
    marginBottom: "4px", // Reduced margin
    color: "rgba(255,255,255,0.95)",
  },
  opponentCards: {
    fontSize: "11px", // Smaller for compact layout
    color: "rgba(255,255,255,0.7)",
  },
  unoBadge: {
    marginTop: "8px",
    padding: "4px 10px",
    background: "var(--uno-inactive)",
    color: "white",
    borderRadius: "12px",
    fontSize: "11px",
    fontWeight: "700",
    display: "inline-block",
  },
  // CENTER ZONE - Table Area (flex: 1)
  centerZone: {
    position: "relative",
    zIndex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "visible", // Allow fan-out cards to extend beyond bounds
    // NO safe-area padding - cards need full screen space
    // Cards can extend into safe-area regions
  },
  discardArea: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    position: "relative",
    zIndex: 2, // Above centerZone for proper stacking
    // No filter effects - clean minimal
    filter: "none",
    // Allow cards and animations to extend beyond bounds (including safe-area)
    overflow: "visible",
    // NO safe-area constraints - cards can extend into safe-area regions
  },
  discardLabel: {
    color: "rgba(255,255,255,0.9)", // Slightly muted for game feel
    fontSize: "16px",
    marginBottom: "12px",
    fontWeight: "600",
    // Subtle glow effect with depth
    textShadow: "0 0 8px rgba(255,255,255,0.1), 0 2px 8px rgba(0,0,0,0.5)",
    letterSpacing: "1px",
  },
  // BOTTOM ZONE - Player Hand (flex: 1)
  bottomZone: {
    position: "relative",
    zIndex: 0,
    overflow: "visible",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-end",
    // Enhanced dark gradient for game feel - deeper shadow at bottom
    background: "linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.4) 50%, transparent 100%)",
    // Hand should slightly overlap center zone for immersion
    marginTop: "-20px", // Overlap with center zone
    boxSizing: "border-box", // Include padding in height calculation
  },
  handHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "12px",
    padding: "0 20px",
    flexWrap: "wrap",
    gap: "8px",
    flexShrink: 0,
  },
  handTitle: {
    fontSize: "18px",
    fontWeight: "700",
    color: "white",
    textShadow: "0 2px 4px rgba(0,0,0,0.3)",
  },
  unoWarning: {
    color: "var(--card-red)",
    fontWeight: "700",
    fontSize: "18px",
    textShadow: "0 0 10px rgba(255, 82, 82, 0.5)",
  },
  turnIndicator: {
    padding: "8px 20px",
    background: "var(--accent)",
    color: "white",
    borderRadius: "25px",
    fontSize: "14px",
    fontWeight: "700",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    // Enhanced depth with glow and inner highlight
    boxShadow: "0 6px 20px rgba(102, 126, 234, 0.5), 0 2px 6px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.2)",
    whiteSpace: "nowrap",
    flexShrink: 0,
    border: "1px solid rgba(255,255,255,0.2)",
  },
  turnIndicatorGlow: {
    animation: "pulse 2s ease-in-out infinite",
  },
  handContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-end",
    minHeight: "160px", // Increased for better mobile visibility and easy tapping
    maxHeight: "180px",
    padding: "12px 0",
    overflowX: "visible",
    overflowY: "visible",
    position: "relative",
    zIndex: 1600, // Above backdrop, below modals
    flexShrink: 0,
  },
  hand: {
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-end",
    position: "relative",
    minWidth: "fit-content",
    overflow: "visible",
  },
  cardWrapper: {
    marginLeft: "-35px",
    position: "relative",
    willChange: "transform",
    pointerEvents: "auto",
    // Physical shadow only - no filter effects
    boxShadow: "0 1px 3px rgba(0,0,0,0.12)", // Ultra-soft shadow for physical feel
  },
  cardWrapperFirst: {
    marginLeft: "0",
  },
  actions: {
    display: "flex",
    gap: "10px",
    justifyContent: "center",
    flexWrap: "wrap",
    marginTop: "12px",
    padding: "0 10px",
    flexShrink: 0,
  },
  actionButton: {
    padding: "10px 20px",
    fontSize: "14px",
    fontWeight: "700",
    color: "white",
    border: "none",
    borderRadius: "12px", // More rounded for game feel
    cursor: "pointer",
    // Snappy button transitions: 200ms
    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
    boxShadow: "0 4px 12px rgba(0,0,0,0.3), 0 2px 4px rgba(0,0,0,0.2)",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    whiteSpace: "nowrap",
    minWidth: "fit-content",
    // Ensure minimum tap target (44px) on mobile
    minHeight: "44px",
    // Active/press feedback
    transform: "translateY(0)",
  },
  drawButton: {
    background: "var(--accent)",
    // Enhanced button depth
    boxShadow: "0 6px 16px rgba(102, 126, 234, 0.4), 0 2px 4px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)",
  },
  drawButtonPulse: {
    animation: "buttonPulse 2s ease-in-out infinite",
  },
  playDrawnButton: {
    background: "var(--btn-success)",
    // Enhanced button depth
    boxShadow: "0 6px 16px rgba(46, 204, 113, 0.4), 0 2px 4px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)",
  },
  unoButton: {
    padding: "10px 20px",
    fontSize: "14px",
    fontWeight: "700",
    color: "white",
    background: "var(--uno-inactive)",
    border: "none",
    borderRadius: "12px", // More rounded
    cursor: "pointer",
    // Snappy transitions: 200ms
    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
    boxShadow: "0 6px 16px rgba(231, 76, 60, 0.5), 0 2px 4px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    minHeight: "44px", // Minimum tap target
  },
  unoButtonUrgent: {
    background: "var(--uno-urgent)",
  },
  unoReminder: {
    position: "fixed",
    bottom: "200px",
    left: "50%",
    transform: "translateX(-50%)",
    padding: "12px 24px",
    background: "var(--warning-bg)",
    // backdropFilter removed for Casual Party (solid backgrounds, no blur)
    color: "var(--warning-text)",
    borderRadius: "12px",
    fontSize: "16px",
    fontWeight: "700",
    zIndex: 5000,
    boxShadow: "0 4px 20px rgba(255, 193, 7, 0.5)",
    pointerEvents: "none",
    textAlign: "center",
  },
  unoSuccessBadge: {
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    padding: "20px 40px",
    background: "linear-gradient(135deg, #2ecc71 0%, #27ae60 100%)",
    color: "white",
    borderRadius: "16px",
    fontSize: "48px",
    fontWeight: "900",
    zIndex: 6000,
    boxShadow: "0 8px 40px rgba(46, 204, 113, 0.6)",
    pointerEvents: "none",
    textShadow: "0 2px 10px rgba(0,0,0,0.3)",
    letterSpacing: "2px",
  },
  unoPenaltyMessage: {
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    padding: "24px 32px",
    background: "rgba(231, 76, 60, 0.95)",
    // backdropFilter removed for Casual Party (solid backgrounds, no blur)
    color: "white",
    borderRadius: "16px",
    zIndex: 6000,
    boxShadow: "0 8px 40px rgba(231, 76, 60, 0.6)",
    pointerEvents: "none",
    textAlign: "center",
    border: "3px solid rgba(255, 255, 255, 0.3)",
  },
  unoPenaltyTitle: {
    fontSize: "24px",
    fontWeight: "900",
    marginBottom: "8px",
    textShadow: "0 2px 10px rgba(0,0,0,0.5)",
  },
  unoPenaltySubtitle: {
    fontSize: "18px",
    fontWeight: "700",
    opacity: 0.9,
  },
  unoPenaltyFlash: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "var(--uno-inactive)",
    zIndex: 5500,
    pointerEvents: "none",
  },
  spectatorMode: {
    background: "rgba(255,255,255,0.1)",
    // backdropFilter removed for Casual Party (solid backgrounds, no blur)
    padding: "40px",
    borderRadius: "16px",
    textAlign: "center",
    border: "1px solid rgba(255,255,255,0.1)",
  },
  spectatorTitle: {
    fontSize: "24px",
    fontWeight: "700",
    color: "white",
    marginBottom: "15px",
  },
  spectatorText: {
    fontSize: "16px",
    color: "rgba(255,255,255,0.8)",
    marginBottom: "20px",
  },
  backToRoomButton: {
    background: "var(--accent)",
    color: "white",
    padding: "12px 24px",
    border: "none",
    borderRadius: "12px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.3s ease",
    boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
  },
};

// Add CSS animations
if (typeof document !== "undefined") {
  const styleSheet = document.createElement("style");
  styleSheet.textContent = `
    @keyframes pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.6; }
    }
    @keyframes buttonPulse {
      0%, 100% { 
        transform: scale(1); 
        box-shadow: 0 6px 16px rgba(102, 126, 234, 0.4), 0 2px 4px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1);
      }
      50% { 
        transform: scale(1.05); 
        box-shadow: 0 8px 24px rgba(102, 126, 234, 0.6), 0 4px 8px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.15);
      }
    }
    
    /* Button press feedback */
    button:active {
      transform: translateY(1px) scale(0.98);
      transition: transform 0.1s ease-out;
    }
    
    /* Card tap feedback enhancement */
    [data-card-wrapper] {
      transition: filter 0.2s ease-out;
    }
  `;
  document.head.appendChild(styleSheet);
}
